import * as THREE from 'three/webgpu';
import { flightModel, setFlightModel } from '../lib/aero_flight.js';
import { cameraMotion, setCameraMotion, shakeOptIn, setShakeOptIn, CAMERA_MOTIONS, type CameraMotion } from '../lib/camera_comfort.js'; // physics follow-up A
import { feel } from '../lib/combat_feel.js';
import { LOOKS, LOOK_LABEL, type LookName } from '../lib/lut_looks.js';
import { setSnowiness } from '../lib/wetness.js';
/**
 * AtlasGraphicsMenuModule — the Esc graphics / options menu.
 *
 * A full-screen overlay (toggle: Esc) in four tabs — General (who and where you are, what draws the
 * picture), Graphics, Sound, Controls (keyboard beside gamepad).
 *
 * GRAPHICS CHANGES WAIT FOR "APPLY CHANGES" (2026-09-20). Every graphics change is a post-pipeline
 * rebuild plus an `atlas:quality-changed` (warm assets evicted, sky LUTs re-baked); the old menu paid
 * that per click, and ~35 flag rows announced nothing at all, so a toggle "did nothing" until some
 * other row rebuilt. The Graphics tab edits a DRAFT (lib/quality_tiers `inDraft`): rows show what is
 * staged and what it "was", a staged preset previews its own values, and ONE button applies the lot.
 * Sound and Controls change as they are set (a level is judged by ear; a binding is one deliberate
 * act). A live FPS / frame-time readout shows the impact so a weak machine can be dialled in.
 *
 * It drives the shared graphics-settings model in `lib/quality_tiers.ts` (the per-
 * feature setters mutate the live `activePreset()`), then calls `PostModule` to
 * rebuild the post pipeline + re-emit `atlas:quality-changed` — which Lighting /
 * Culling / Streaming / Weather / Atmosphere already react to. No new plumbing: the
 * menu is a UI over the quality system that already scales the whole pipeline.
 */
import type { App, IModule } from 'enginex';
import { makeSystem, Phase } from '../engine-compat.js';
import { versonaTts } from '../lib/tts/versona_tts.js';
import { audioRuntime } from '../lib/audio/runtime.js';
import { OUTPUT_MODES, VOLUME_GROUPS, VOLUME_LABEL, type OutputMode } from '../lib/audio/buses.js';
import {
  groundKnobs, setGroundMaterial, setGroundArrayRes, setGroundTriplanar, setGroundPom, setGroundNearSeg,
  TIER_ORDER, TIER_LABEL, activeTier, isCustom, activePreset, setRenderScale, setAA, setShadows, setBloom, setSSAO, setDoF, setClouds, setModelSmoothing, setSurfaceDetail, setParticleDensity, setDrawDistance, setDistantDetail, distantDetailOn, MIN_DRAW_DISTANCE_M, MIN_RENDER_SCALE, RESOLUTION_TARGETS, setTargetHeight, setPlacementLod, setSsgi, setLensFlares, setLook, setSsr, setGodRays, setContactShadows, setVolumetricFogPass, setFoliageShading, setFoliageWind, currentFoliageWind, setMaterialFx, setInteriorMapping, setInteriorDetail, setVfxShaders, setLitDecals, setLampCookies, setWaterFoam, setFluidPhysics, setWaterCaustics, setCloudShadows, setGroundAntiTiling, setRainRipples, setAurora, setNebulae, setPlanetAtmosphere, setSpaceSky, setSpaceMotion, setSpacePlumes, setSpaceLitHulls, setSpaceShields, setUnderwaterShafts, setDqSkinning, setBloomMode, currentBloomMode, setSettlementBuildLod, setRiverWater, setGroundQuality, setSkyQuality, currentAA, currentShadows, setVignette, setSharpen, setSaturation, setMotionBlur, setFilmGrain, setChromaticAberration, setCapsuleShadows, setAerialPerspective, setAutoExposure, setBokehStyle, setLowClouds, setCloudParallax, setAAQuality, setFogDistance, setVolFogDistance, setBloomStrength, setMotionBlurIntensity, setAutoExposureStrength, setDofFocusDistance, setDofFocalLength, setDofBokehScale, currentVignette, currentSharpen, currentSaturation, currentVolumetricFogPass, currentMotionBlur, currentFilmGrain, currentChromaticAberration, currentCapsuleShadows, currentAerialPerspective, currentAutoExposure, currentBokehStyle, currentLowClouds, currentCloudParallax, currentAAQuality, currentFogDistance, currentVolFogDistance, currentDrawDistance, currentBloomStrength, currentMotionBlurIntensity, currentAutoExposureStrength, currentDofFocusDistance, currentDofFocalLength, currentDofBokehScale, setFoliageDensity, setLodDistanceScale, setSettlementDetail, setMaxTextureRes, setAnisotropy, setTonemapExposure, setTonemapOperator, setExposureMode, currentExposureMode, setHighClouds, currentHighClouds, setCloudMarchQuality, setWaterReflections, setColorblindMode, setColorblindStrength, setCameraFov, setAutoTune, setBloomThreshold, setBloomRadius, setAoIntensity, setAoRadius, setVignetteStrength, setFilmGrainIntensity, setChromaticStrength, setMotionBlurSamples, type AAMode, type ShadowLevel, setUpscale, setAdaptive, setUpscaleMode, setCloudResolution, type UpscaleMode, effectivePixelRatio,
  inDraft, inLive, draftChanges, commitDraft, discardDraft, setActiveTier,
  setSpecularAA, setProceduralFiltering, setVolumetricLights, setLitSmoke, setAreaLights, setGlassTransmission, setHardSurfaceAnisotropy, setRetroreflection, setAutoExposureMetering, currentAutoExposureMetering, setImpostorBlend, setRainShelter,
  setFireQuality, setFireHaze, setFireLights,
  setGrassLayer, setGeologyDensity, setUnderstoryDensity, setForestHorizon, setFarCanopy, setFoliageJitter, setAmbientLife, setCaveLight, setMaterialDamage, setSkinShading, setWetSurfaces, setSurfaceDeform, setVolumeResolution, setVolumeNoise, setEclipseShadows, setPlanetRings, setOrbitalClouds, type TierPreset,
} from '../lib/quality_tiers.js';
import type { PostModule } from './PostModule.js';
import { setWaterLook, type WaterLookKey } from '../lib/quality_tiers.js'; // water agent (2026-09-25)
import { graphicsHelp } from '../lib/graphics_help.js'; // the "?" beside every graphics row (2026-09-29)
import { setBlackHole } from '../lib/quality_tiers.js'; // shader gaps: black hole
import { setPuddleSim } from '../lib/quality_tiers.js'; // shader gaps R6: the puddle patch
import { setGroundMicroShadow, setGroundHexTiling, setGroundPomShadow } from '../lib/quality_tiers.js'; // surfaces (2026-09-27): the three ground rows
import { setEonDiffuse, setLaplacianBlend, setFlakeGlints } from '../lib/quality_tiers.js'; // shader gaps R4: material models
import { setWindowShadows } from '../lib/quality_tiers.js'; // shader gaps R5: window-light shadows
import { setFftOcean } from '../lib/quality_tiers.js'; // shader gaps R5: the FFT ocean
// materials agent (2026-09-25): grime, mip bias, AA-before-upscale, the EASU kernel, the camera lens, the character layers
import { setMaterialGrime, setMipBias, setPropSurfaceDetail, setCameraDof, setCameraFNumber, setPlayerBodyRig, setFootIk, setBreathing, setAaFirst, setEasuKernel, currentAaFirst, currentEasuKernel } from '../lib/quality_tiers.js';
import { hFovFromV, vFovFromH, REFERENCE_ASPECT, FIRST_PERSON_VFOV } from '../lib/camera_model.js';
/** Water realism wave (2026-09-25): the Water group's look rows — [key, label, note]. */
const WATER_LOOK_ROWS: ReadonlyArray<[WaterLookKey, string, string]> = [
  ['waterSkyReflection', 'Sky reflection on water', 'needs the full sky'],
  ['waterCloudReflection', 'Clouds in water reflections', 'medium and up'],
  ['waterSpectrumNormals', 'Spectrum wave detail', 'no tile lattice'],
  ['waterScattering', 'Crest light scattering', 'backlit crests glow'],
  ['waterGlitterFilter', 'Distance-aware sun glitter', 'a broad sun road, no sparkle noise'],
  ['waterFarSea', 'Far sea matches the near sea', 'with distant detail'],
  ['waterAnalyticBed', 'Shallows show the bed', 'sand and ripples under clear water'],
  ['waterBedCaustics', 'Caustics on the seabed', 'at the next world'],
  ['waterUnderwaterMedium', 'Underwater colour absorption', 'red fades first'],
  ['waterMarineLife', 'Marine snow and bubbles', 'needs particles'],
  ['waterCalmLakes', 'Calm glassy lakes', 'wind patches ruffle them'],
  ['waterfallFx', 'Waterfall mist, rainbow and strands', 'needs particles'],
  ['waterReflectionBudget', 'One planar reflection at a time', 'with water reflections'],
  ['waterSplashCrowns', 'Splash crowns', 'needs particles'],
  ['waterFoamField', 'Foam trails (GPU)', 'with sea foam trails'],
  ['waterRiverFlow', 'River flow and white water', 'rapids foam, fast water rougher'],
];
import { bus } from '../bus.js';
import { THEMES, THEME_ORDER, DEFAULT_THEME, currentTheme, setTheme, type ThemeId } from '../lib/ui_theme.js';
import { atlas } from '../state.js';
import { escOwner, ESC_SHOW_EXCLUDE } from '../lib/esc_order.js';
import { topNavPanel } from '../lib/ui_nav.js';
import { authStatus, getToken, clearToken } from '../lib/auth_store.js'; // THE LOGIN (2026-10-05): Options → General → Account
import { authApi } from '../lib/auth_client.js';
import { translateOn, setTranslateOn, myChatLang, setMyChatLang, wireChatTranslate, supportedLangs, langName } from '../lib/chat_translate.js'; // G4: Options → General → Chat translation
import {
  padEnabled, setPadEnabled, padSnap, padEverSeen, padBindingText, padBindings, setPadBinding,
  resetPadBindings, padBeginCapture, padCancelCapture, padCaptureActive, padCaptureSeq,
  padLastCapture, padRumble, padRumbleEnabled, setPadRumble, padRumbleGain,
  setPadRumbleGain, padHasActuator, PAD_CONTEXTS, PAD_BUTTON_LABEL, type PadActionName,
} from '../lib/gamepad.js';
import {
  keyBindings, setKeyBinding, resetKeyBindings, keyBeginCapture, keyCancelCapture,
  keyCaptureActive, keyCaptureSeq, keyLastCapture, keyLabel, KEY_CONTEXTS, keySavedConflicts,
} from '../lib/keybinds.js';
import { beamMode, setBeamMode } from '../lib/space_input.js';
import { desktopSpaceComfort, setDesktopSpaceComfort, HUD_SCALE_CHOICES, type SectorMotion } from '../lib/space_comfort.js'; // space campaign L
import { TOUCH_LABEL } from '../lib/touch_layout.js';

export class AtlasGraphicsMenuModule implements IModule {
  readonly name = 'GraphicsMenu';
  private app!: App;
  private overlay: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private tabsEl: HTMLDivElement | null = null;
  private railEl: HTMLElement | null = null;
  private footEl: HTMLDivElement | null = null;
  private fpsEl: HTMLDivElement | null = null;
  private open = false;
  private tab: Tab = 'general';
  private renderedTab: Tab | null = null;
  /** The Graphics tab's groups (rail link + heading) and how many rows differ from the live settings. */
  private groups: Array<{ head: HTMLElement; link: HTMLElement; changed: number }> = [];
  private changedRows = 0;
  private appliedUntil = 0;
  /** `data-k` of the control last used: a re-render hands the focus back to it. */
  private focusKey = '';
  // The "?" tooltip (2026-09-29): ONE element inside the overlay, placed beside the "?" it explains. Hover or focus
  // shows it; a click pins it until another click, Esc, a scroll or a re-render.
  private tipEl: HTMLDivElement | null = null;
  private tipFor: HTMLButtonElement | null = null;
  private tipPinned = false;
  private tipHideTimer: ReturnType<typeof setTimeout> | null = null;
  /** General tab values refreshed in place (position, frame rate). */
  private liveEls: Record<string, HTMLElement> = {};
  private fpsText = '';
  private bindSeq = 0;
  private bindActive = '';
  private signOutConfirm = false; // F1: the unclaimed traveler's sign-out confirm is showing
  private keyHandler: ((e: KeyboardEvent) => void) | null = null;
  // FPS moving average over ~0.5 s windows.
  private fpsAccumMs = 0;
  private fpsFrames = 0;

  init(app: App): void {
    this.app = app;
    // THE TOUR'S RAW SETTERS (2026-09-18, the shader tour): every switchable shader by ONE key, applied WITHOUT the
    // per-setter apply — `set` / `setMany` below apply once (the post rebuild is coalesced per frame anyway; this keeps
    // the quality-changed bus quiet across a 50-flag leg). lib/shader_tour.ts names these keys.
    const RAW: Record<string, (v: unknown) => void> = {
      bloom: (v) => setBloom(!!v), ao: (v) => setSSAO(!!v), dof: (v) => setDoF(!!v), motionBlur: (v) => setMotionBlur(!!v), vignette: (v) => setVignette(!!v),
      sharpen: (v) => setSharpen(typeof v === 'number' ? v : v ? 0.5 : 0), film: (v) => setFilmGrain(!!v), chromaticAberration: (v) => setChromaticAberration(!!v), capsuleShadows: (v) => setCapsuleShadows(!!v),
      ssr: (v) => setSsr(!!v), contactShadows: (v) => setContactShadows(!!v), ssgi: (v) => setSsgi(!!v), lensFlares: (v) => setLensFlares(!!v), godRays: (v) => setGodRays(!!v), volumetricFog: (v) => setVolumetricFogPass(!!v),
      aa: (v) => setAA((v === 'smaa' || v === 'fxaa' || v === 'traa' ? v : 'off') as Parameters<typeof setAA>[0]), bloomMode: (v) => setBloomMode(v === 'kawase' ? 'kawase' : 'mip'), look: (v) => setLook((typeof v === 'string' ? v : 'none') as LookName),
      autoExposure: (v) => setAutoExposure(!!v), autoExposureMetering: (v) => setAutoExposureMetering(v === 'histogram' ? 'histogram' : 'average'), aerialPerspective: (v) => setAerialPerspective(!!v),
      tonemap: (v) => setTonemapOperator((['aces', 'agx', 'aces2', 'neutral', 'reinhard'].includes(String(v)) ? v : 'agx') as 'aces' | 'agx' | 'aces2' | 'neutral' | 'reinhard'), exposureMode: (v) => setExposureMode(v === 'fixed' ? 'fixed' : 'physical'),
      colorblind: (v) => setColorblindMode((typeof v === 'string' ? v : 'off') as Parameters<typeof setColorblindMode>[0]),
      clouds: (v) => setClouds(!!v), lowClouds: (v) => setLowClouds(!!v), highClouds: (v) => setHighClouds(!!v), cloudShadows: (v) => setCloudShadows(!!v), volumetricLights: (v) => setVolumetricLights(!!v), areaLights: (v) => setAreaLights(!!v), lampCookies: (v) => setLampCookies(!!v), distantDetail: (v) => setDistantDetail(!!v),
      foliageShading: (v) => setFoliageShading(!!v), foliageWind: (v) => setFoliageWind(!!v), materialFx: (v) => setMaterialFx(!!v), interiorMapping: (v) => setInteriorMapping(!!v), interiorDetail: (v) => setInteriorDetail((Number(v) === 0 ? 0 : Number(v) === 2 ? 2 : 1) as 0 | 1 | 2) /* interiors programme, slice D */, vfxShaders: (v) => setVfxShaders(!!v), litDecals: (v) => setLitDecals(!!v),
      specularAA: (v) => setSpecularAA(!!v), proceduralFiltering: (v) => setProceduralFiltering(!!v), glassTransmission: (v) => setGlassTransmission(!!v), hardSurfaceAnisotropy: (v) => setHardSurfaceAnisotropy(!!v), retroreflection: (v) => setRetroreflection(!!v), litSmoke: (v) => setLitSmoke(!!v),
      // Fire realism (2026-09-25): the fire shader rung + the heat haze over the flames.
      fireQuality: (v) => setFireQuality((v === 'sprite' || v === 'cards' || v === 'sim' ? v : 'volume') as 'sprite' | 'cards' | 'volume' | 'sim'), fireHaze: (v) => setFireHaze(!!v),
      dqSkinning: (v) => setDqSkinning(!!v), groundAntiTiling: (v) => setGroundAntiTiling(!!v), rainRipples: (v) => setRainRipples(!!v),
      skinShading: (v) => setSkinShading(!!v), // shader gaps R2: skin on the near rig actors (lib/skin_*.ts)
      eonDiffuse: (v) => setEonDiffuse(!!v), laplacianBlend: (v) => setLaplacianBlend(!!v), flakeGlints: (v) => setFlakeGlints(!!v), // shader gaps R4: material models
      windowShadows: (v) => setWindowShadows(!!v), // shader gaps R5: window-light shadows
      wetSurfaces: (v) => setWetSurfaces((v === 'off' || v === 'ground' ? v : 'all') as 'off' | 'ground' | 'all'), surfaceDeform: (v) => setSurfaceDeform(!!v), // shader gaps R3: weather on surfaces
      puddleSim: (v) => setPuddleSim((v === 'small' || v === 'medium' || v === 'large' ? v : 'off') as 'off' | 'small' | 'medium' | 'large'), // shader gaps R6: the puddle patch
      volumeResolution: (v) => setVolumeResolution(v === 'full' ? 'full' : 'half'), volumeNoise: (v) => setVolumeNoise(v === 'stbn' || v === 'white' ? v : 'golden'), // shader gaps R3 (agent F): local volumes
      // Ground material v2 (2026-09-25): the scanned layers by rules, their array detail, triplanar rock, parallax, the near mesh.
      groundMaterial: (v) => setGroundMaterial(v === 'v1' ? 'v1' : 'v2'), groundArrayRes: (v) => setGroundArrayRes(([512, 2048, 4096, 8192] as const).find((r) => r === Number(v)) ?? 1024), // ground (2026-09-26): + 4K / 8K
      // materials hand-off (2026-09-27): this setter sat INSIDE the comment above since 2026-09-26 — the Triplanar rock row never applied.
      groundTriplanar: (v) => setGroundTriplanar(!!v),
      groundPom: (v) => setGroundPom(v === 'rock' || v === 'all' ? v : 'off'), groundNearSeg: (v) => setGroundNearSeg(Number(v) === 96 ? 96 : 48),
      groundMicroShadow: (v) => setGroundMicroShadow(!!v), groundHexTiling: (v) => setGroundHexTiling(!!v), groundPomShadow: (v) => setGroundPomShadow(!!v), // surfaces (2026-09-27)
      waterReflections: (v) => setWaterReflections(!!v), waterFoam: (v) => setWaterFoam(!!v), waterCaustics: (v) => setWaterCaustics(!!v), fftOcean: (v) => setFftOcean(!!v), /* shader gaps R5 */ fluidPhysics: (v) => setFluidPhysics(!!v), underwaterShafts: (v) => setUnderwaterShafts(!!v),
      // Water realism wave (2026-09-25): every WATER_TIERS switch by its own key.
      ...Object.fromEntries(WATER_LOOK_ROWS.map(([k]) => [k, (v: unknown) => setWaterLook(k, !!v)])),
      aurora: (v) => setAurora(!!v), nebulae: (v) => setNebulae(!!v), planetAtmosphere: (v) => setPlanetAtmosphere(!!v),
      // The space realism wave (2026-09-25, the space agent):
      spaceSky: (v) => setSpaceSky(!!v), spaceMotion: (v) => setSpaceMotion(!!v), spacePlumes: (v) => setSpacePlumes(!!v), spaceLitHulls: (v) => setSpaceLitHulls(!!v), spaceShields: (v) => setSpaceShields(!!v), eclipseShadows: (v) => setEclipseShadows(!!v), planetRings: (v) => setPlanetRings(!!v), orbitalClouds: (v) => setOrbitalClouds(!!v), /* shader gaps R4 */ blackHole: (v) => setBlackHole(v === 'off' || v === 'low' ? v : 'high'), /* shader gaps: black hole */ upscaleMode: (v) => setUpscaleMode(v === 'taau' ? 'taau' : 'fsr'),
      // Vegetation realism (2026-09-25): the grass layer, geology, understory, the forest horizon, the far canopy, jitter, ambient life.
      grassLayer: (v) => setGrassLayer((typeof v === 'string' ? v : v ? 'high' : 'off') as TierPreset['grassLayer']), geology: (v) => setGeologyDensity(typeof v === 'number' ? v : v ? 1 : 0),
      understory: (v) => setUnderstoryDensity(typeof v === 'number' ? v : v ? 1 : 0), forestHorizon: (v) => setForestHorizon(typeof v === 'number' ? v : v ? 4000 : 0),
      farCanopy: (v) => setFarCanopy(!!v), foliageJitter: (v) => setFoliageJitter(!!v), ambientLife: (v) => setAmbientLife(!!v), caveLight: (v) => setCaveLight((v === 'off' || v === 'visibility' || v === 'baked' ? v : 'baked+probe') as 'off' | 'visibility' | 'baked' | 'baked+probe'), materialDamage: (v) => setMaterialDamage((v === 'off' || v === 'marks' ? v : 'full') as 'off' | 'marks' | 'full'),
      // Materials / AA / camera / characters realism (2026-09-25).
      materialGrime: (v) => setMaterialGrime(!!v), mipBias: (v) => setMipBias(!!v), propSurfaceDetail: (v) => setPropSurfaceDetail(!!v), aaFirst: (v) => setAaFirst(!!v), easuKernel: (v) => setEasuKernel(v === 'legacy' ? 'legacy' : 'amd'),
      cameraDof: (v) => setCameraDof(v === 'thirdPerson' || v === true ? 'thirdPerson' : 'off'), playerBodyRig: (v) => setPlayerBodyRig(!!v), footIk: (v) => setFootIk(!!v), breathing: (v) => setBreathing(!!v),
    };
    (window as unknown as { __gfx?: unknown }).__gfx = {
      // Probes open the menu through the module (Esc yields to any `.show` panel — the load screen, the traveler screen).
      openMenu: (tab?: string): boolean => { const t = TABS.find(([id]) => id === tab)?.[0]; if (!this.open) this.toggle(t); else if (t) this.setTab(t); return this.open; },
      closeMenu: (): boolean => { if (this.open) this.toggle(); return this.open; },
      // The menu's own state (probes): the open tab, what the Graphics draft would change, how many rows show it.
      menu: () => ({ open: this.open, tab: this.tab, pending: draftChanges(), changedRows: this.changedRows }),
      menuApply: (): boolean => { const had = draftChanges().length > 0; this.applyDraft(); return had; },
      menuDiscard: (): void => this.discard(),
      tier: () => activeTier(),
      set: (key: string, value: unknown): boolean => { const fn = RAW[key]; if (!fn) return false; fn(value); this.apply(); return true; },
      setMany: (entries: Array<[string, unknown]>): number => { let n = 0; for (const [k, v] of entries) { const fn = RAW[k]; if (fn) { fn(v); n++; } } this.apply(); return n; },
      rawKeys: () => Object.keys(RAW),
      motionBlur: (on: boolean) => { setMotionBlur(on); this.apply(); }, // probes: flip the pass (+ rebuild) without the menu
      clouds: () => activePreset().cloudsEnabled,
      setClouds: (on: boolean) => { setClouds(on); this.apply(); }, // probes: the volumetric-clouds toggle (+ rebuild)
      farCull: () => activePreset().farCull,
      chunkRadius: () => activePreset().chunkRadius,
      settlementScale: () => activePreset().settlementRadiusScale,
      fogDistance: () => currentFogDistance(),
      drawDistance: () => currentDrawDistance(),
      dofFocus: () => currentDofFocusDistance(),
      setTier: (t: string) => this.postMod()?.setTier(t as never),
      setDrawDistance: (m: number) => { setDrawDistance(m); this.apply(); },
      distantDetail: () => distantDetailOn(),
      preset: () => ({ ...activePreset() }),
      setResolution: (h: number) => { setTargetHeight(h); this.apply(); },
      // Shader wave levers (probes flip every pass without the menu):
      setSsgi: (on: boolean) => { setSsgi(on); this.apply(); },
      setLensFlares: (on: boolean) => { setLensFlares(on); this.apply(); },
      setLook: (l: string) => { setLook(l as LookName); this.apply(); },
      setSsr: (on: boolean) => { setSsr(on); this.apply(); },
      ao: () => !!activePreset().post.ao,
      setAo: (on: boolean) => { setSSAO(on); this.apply(); }, // probes: the GTAO pass on/off (+ rebuild) — the AO overlay probe
      setGodRays: (on: boolean) => { setGodRays(on); this.apply(); },
      setContactShadows: (on: boolean) => { setContactShadows(on); this.apply(); },
      setVolumetricFog: (on: boolean) => { setVolumetricFogPass(on); this.apply(); },
      passes: () => Object.keys(activePreset().post),
      setFoliageShading: (on: boolean) => { setFoliageShading(on); this.apply(); },
      setFoliageWind: (on: boolean) => { setFoliageWind(on); this.apply(); },
      foliageWind: () => currentFoliageWind(),
      setFluidPhysics: (on: boolean) => { setFluidPhysics(on); this.render(); }, // live: the water module reads the flag every tick
      fluidPhysics: () => activePreset().fluidPhysics !== false,
      setMaterialFx: (on: boolean) => { setMaterialFx(on); this.apply(); },
      // Shader wave stage 3 levers (each usable on any tier).
      setInteriorMapping: (on: boolean) => { setInteriorMapping(on); this.apply(); },
      setVfxShaders: (on: boolean) => { setVfxShaders(on); this.apply(); },
      setLitDecals: (on: boolean) => { setLitDecals(on); this.apply(); },
      setLampCookies: (on: boolean) => { setLampCookies(on); this.apply(); },
      setWaterFoam: (on: boolean) => { setWaterFoam(on); this.apply(); },
      setWaterCaustics: (on: boolean) => { setWaterCaustics(on); this.apply(); },
      // Shader wave stage 4 levers.
      setCloudShadows: (on: boolean) => { setCloudShadows(on); this.apply(); },
      setGroundAntiTiling: (on: boolean) => { setGroundAntiTiling(on); this.apply(); },
      setRainRipples: (on: boolean) => { setRainRipples(on); this.apply(); },
      setAurora: (on: boolean) => { setAurora(on); this.apply(); },
      setNebulae: (on: boolean) => { setNebulae(on); this.apply(); },
      setPlanetAtmosphere: (on: boolean) => { setPlanetAtmosphere(on); this.apply(); },
      setUnderwaterShafts: (on: boolean) => { setUnderwaterShafts(on); this.apply(); },
      setDqSkinning: (on: boolean) => { setDqSkinning(on); this.apply(); },
      // Shader additions wave (2026-09-15).
      setSpecularAA: (on: boolean) => { setSpecularAA(on); this.render(); },
      setProceduralFiltering: (on: boolean) => { setProceduralFiltering(on); this.render(); },
      setVolumetricLights: (on: boolean) => { setVolumetricLights(on); this.render(); },
      setLitSmoke: (on: boolean) => { setLitSmoke(on); this.render(); },
      setAreaLights: (on: boolean) => { setAreaLights(on); this.render(); },
      setGlassTransmission: (on: boolean) => { setGlassTransmission(on); this.render(); },
      setHardSurfaceAnisotropy: (on: boolean) => { setHardSurfaceAnisotropy(on); this.render(); },
      setRetroreflection: (on: boolean) => { setRetroreflection(on); this.render(); },
      setAutoExposureMetering: (m: 'average' | 'histogram') => { setAutoExposureMetering(m); this.apply(); },
      setBloomMode: (m: 'mip' | 'kawase') => { setBloomMode(m); this.apply(); },
      setFoliageDensity: (v: number) => { setFoliageDensity(v); this.apply(); }, // probes: the density slider without the menu
      snow: (v: number | null) => { const w = this.app?.getModule('Weather') as { setDebugSnow?: (v: number | null) => void } | undefined; if (w?.setDebugSnow) w.setDebugSnow(v); else if (v !== null) setSnowiness(v); },
      // The canvas as the renderer sees it: the applied pixel ratio + the drawing buffer (probes).
      drawingBuffer: () => { const n = this.app?.renderer.native; if (!n) return null; const v = n.getDrawingBufferSize(new THREE.Vector2()); return { pixelRatio: n.getPixelRatio(), w: v.x, h: v.y, want: effectivePixelRatio() }; },
      sceneFogDensity: () => ((this.app?.world.scene as unknown as { fog?: { density?: number } } | undefined)?.fog?.density ?? null),
      setDistantDetail: (on: boolean) => { setDistantDetail(on); this.apply(); },
      setDof: (on: boolean) => { setDoF(on); this.apply(); },
      setDofFocus: (m: number) => { setDofFocusDistance(m); this.apply(); },
    };
    this.buildOverlay();
    // The gamepad's Start button opens and closes this menu. It arrives as a bus
    // event rather than a synthetic Escape: this codebase never fakes a
    // KeyboardEvent, and a fake one would also have to re-run the arbitration
    // below (charts own Escape while they are up).
    bus.on('atlas:toggle-options', () => {
      if (document.getElementById('space-map') || document.getElementById('galaxy-map')) return;
      this.toggle();
    });
    // C2: a cinematic's Esc is the SKIP (DeviceModule, bubble phase) — this capture handler must not also open Options on it.
    let cinematicPlaying = false;
    bus.on('atlas:cinematic', (c) => { cinematicPlaying = c.playing; });
    this.keyHandler = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.repeat) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? '';
      // This runs in the CAPTURE phase — BEFORE the other panels' (bubble-phase) Esc handlers — so it arbitrates which
      // one Esc belongs to. The ORDER is lib/esc_order.ts (pure, pinned by verify:esc-order); this only reads the DOM.
      const top = topNavPanel();
      const owner = escOwner({
        chartOpen: !!(document.getElementById('space-map') || document.getElementById('galaxy-map')), // an open chart owns Escape (key-collision audit 2026-08-24)
        typing: tag === 'INPUT' || tag === 'TEXTAREA',
        optionsOpen: this.open,
        placementView: !!atlas.placementView, // the placement map owns Esc (2026-09-25): that press leaves the map
        cinematic: cinematicPlaying,
        // This Esc is releasing the canvas pointer-lock — let the browser do that and DON'T pop the menu on the same press.
        pointerLocked: !!document.pointerLockElement,
        // Panels mark themselves `.show` when open. The always-on HUDs (#wallet, #pulse) and the account banner (#auth-banner,
        // permanent until Later — C1) are ALSO `.show`: excluded, or the menu could never open for an unclaimed player.
        modalShown: !!document.querySelector(`.show${ESC_SHOW_EXCLUDE}`),
        windowCloseable: !!top?.querySelector('[data-atlas-close]'),
      });
      switch (owner) {
        case 'yield': return;
        case 'close-options':
          // My menu is open → close it and consume Esc so no panel also reacts. A shown "?" tooltip takes the first press.
          e.preventDefault();
          e.stopPropagation();
          if (this.tipFor) { this.hideTip(); return; }
          this.toggle();
          return;
        case 'close-window':
          // G2: the keyboard's twin of the pad's B — click the topmost window's own × so its teardown runs.
          e.preventDefault();
          e.stopPropagation();
          top!.querySelector<HTMLElement>('[data-atlas-close]')!.click();
          return;
        case 'open-options':
          e.preventDefault();
          this.toggle();
          return;
      }
    };
    window.addEventListener('keydown', this.keyHandler, true); // capture: arbitrate before panel handlers
    app.world.addSystem(makeSystem('GraphicsMenuFps', Phase.UI, 91, (dt) => this.updateFps(dt)));
    // A rebind resolves inside the pad's Input-phase tick, so the panel watches
    // for it here rather than polling the controller a second time.
    app.world.addSystem(makeSystem('GraphicsMenuRebind', Phase.UI, 92, () => {
      if (!this.open) return;
      const seq = padCaptureSeq() + keyCaptureSeq() * 1000;
      const active = `${padCaptureActive() ?? ''}|${keyCaptureActive() ?? ''}`;
      if (seq !== this.bindSeq || active !== this.bindActive) { this.bindSeq = seq; this.bindActive = active; this.render(); }
    }));
    window.setTimeout(() => this.applyFov(), 1500); // persisted FOV once the camera exists
  }

  private postMod(): PostModule | undefined { return this.app.getModule<PostModule>('Post'); }
  /** An IMMEDIATE change — the probe levers on `__gfx`. The menu's own Graphics rows go through the draft (`stage`). */
  private apply(): void { this.postMod()?.applySettings(); this.render(); }
  /** Stage a graphics change: it edits the DRAFT (lib/quality_tiers) and waits for "Apply changes". */
  private stage(fn: () => void): void { inDraft(fn); this.render(); }
  /**
   * "Apply changes": the draft becomes the live settings — ONE pipeline rebuild and ONE `atlas:quality-changed` for
   * any number of rows. (Before 2026-09-20 every row rebuilt on its own click, and the ~35 flag rows announced
   * nothing at all: a toggle "did nothing" until some other row happened to rebuild.)
   */
  private applyDraft(): void {
    if (!commitDraft()) { this.render(); return; }
    this.postMod()?.applySettings();
    this.applyFov();
    (this.app.getModule('Clouds') as { setParallaxScale?(x: number): void } | undefined)?.setParallaxScale?.(currentCloudParallax());
    this.appliedUntil = performance.now() + 2600;
    this.render();
    // The "Applied" note expires with one re-render — but a re-render hides a shown "?" tooltip (its button is rebuilt), so it
    // waits while one is up (a pinned tip was closed under the reader's cursor 2.7 s after any Apply; probe 7 raced it).
    const expire = (): void => { if (!this.open) return; if (this.tipFor) { window.setTimeout(expire, 1000); return; } this.render(); };
    window.setTimeout(expire, 2700);
  }
  private discard(): void { discardDraft(); this.appliedUntil = 0; this.render(); }

  private setTab(tab: Tab): void {
    this.tab = tab;
    try { localStorage.setItem('atlas:options-tab', tab); } catch { /* storage denied */ }
    this.render();
  }

  private toggle(tab?: Tab): void {
    this.open = !this.open;
    if (this.open && tab) this.tab = tab;
    if (!this.open) this.hideTip();
    if (this.overlay) this.overlay.style.display = this.open ? 'flex' : 'none';
    if (this.open) this.render();
  }

  private buildOverlay(): void {
    if (!document.getElementById('atlas-options-style')) {
      const st = document.createElement('style');
      st.id = 'atlas-options-style';
      st.textContent = OPTIONS_CSS;
      document.head.appendChild(st);
    }
    const overlay = document.createElement('div');
    overlay.id = 'atlas-options';
    overlay.dataset.atlasNav = 'options';   // the gamepad's UI navigator finds it here
    const panel = el('div', 'op-panel');
    panel.setAttribute('role', 'dialog');
    panel.setAttribute('aria-label', 'Options');

    const head = el('div', 'op-head');
    head.appendChild(el('div', 'op-title', 'Options'));
    this.fpsEl = el('div', 'op-fps');
    this.fpsEl.append(el('span', '', '– fps'), el('span', '', '– ms'));
    const close = btn('Close', () => this.toggle(), 'op-btn op-close');
    close.dataset.atlasClose = '1';        // the gamepad's B closes through this
    close.title = 'Close the menu (Esc)';
    head.append(this.fpsEl, close);

    this.tabsEl = el('div', 'op-tabs');
    this.tabsEl.setAttribute('role', 'tablist');
    const main = el('div', 'op-main');
    this.railEl = el('nav', 'op-rail');
    this.railEl.setAttribute('aria-label', 'Graphics groups');
    this.body = el('div', 'op-body');
    this.body.addEventListener('scroll', () => { this.spy(); if (this.tipFor) this.hideTip(); }, { passive: true });
    main.append(this.railEl, this.body);
    this.footEl = el('div', 'op-apply');
    this.footEl.setAttribute('aria-live', 'polite');
    panel.append(head, this.tabsEl, main, this.footEl);
    overlay.appendChild(panel);
    // Click the dimmed backdrop (outside the panel) closes the menu.
    overlay.addEventListener('click', (e) => { if (e.target === overlay) this.toggle(); });
    // A click anywhere but a "?" or the tooltip itself puts a pinned tooltip away.
    overlay.addEventListener('click', (e) => { const t = e.target as HTMLElement | null; if (this.tipPinned && !t?.closest('.op-help, .op-tip')) this.hideTip(); }, true);
    // Remember WHICH control was used, so the re-render that follows a click hands the focus back to it
    // (keyboard and gamepad alike — a pad player was thrown to the top of the list after every change).
    overlay.addEventListener('click', (e) => { const k = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-k]')?.dataset.k; if (k) this.focusKey = k; }, true);
    document.body.appendChild(overlay);
    this.overlay = overlay;
    try { const t = localStorage.getItem('atlas:options-tab'); if (t && TABS.some(([id]) => id === t)) this.tab = t as Tab; } catch { /* storage denied */ }
  }

  /** Re-render the open tab from the current settings. */
  private render(): void {
    if (!this.body || !this.tabsEl || !this.railEl || !this.footEl) return;
    this.hideTip(); // the "?" it belongs to is about to be replaced
    const keepScroll = this.renderedTab === this.tab ? this.body.scrollTop : 0;
    const pending = draftChanges();
    this.tabsEl.replaceChildren(...TABS.map(([id, label]) => {
      const b = btn(label, () => this.setTab(id), 'op-tab');
      b.dataset.tab = id;                  // the pad's shoulders walk these
      b.dataset.k = `tab:${id}`;
      b.setAttribute('role', 'tab');
      b.setAttribute('aria-selected', String(id === this.tab));
      if (id === 'graphics' && pending.length) { b.dataset.dirty = '1'; b.title = 'Graphics changes are waiting to be applied'; }
      return b;
    }));
    this.body.replaceChildren();
    this.railEl.replaceChildren();
    this.groups = [];
    this.changedRows = 0;
    this.liveEls = {};
    this.railEl.style.display = this.tab === 'graphics' ? '' : 'none';
    if (this.tab === 'graphics') inDraft(() => this.renderGraphics());
    else if (this.tab === 'sound') this.renderSound();
    else if (this.tab === 'controls') this.renderControls();
    else this.renderGeneral();
    this.renderFoot(pending);
    this.body.scrollTop = keepScroll;
    this.renderedTab = this.tab;
    this.spy();
    if (this.focusKey) {
      const again = this.overlay?.querySelector<HTMLElement>(`[data-k="${CSS.escape(this.focusKey)}"]`);
      if (again && document.activeElement !== again) { try { again.focus({ preventScroll: true }); } catch { /* not focusable */ } }
    }
  }

  // ── The Apply bar ────────────────────────────────────────────────────────────
  private renderFoot(pending: string[]): void {
    const foot = this.footEl!;
    foot.replaceChildren();
    foot.style.display = this.tab === 'graphics' ? '' : 'none';
    if (this.tab !== 'graphics') return;
    const has = pending.length > 0;
    foot.classList.toggle('is-pending', has);
    const stagedTier = has && pending.includes('preset') ? inDraft(() => TIER_LABEL[activeTier()]) : null;
    const n = Math.max(1, this.changedRows);
    const text = has
      ? (stagedTier ? `${stagedTier} preset waiting to be applied` : `${n} ${n === 1 ? 'change' : 'changes'} waiting to be applied`)
      : performance.now() < this.appliedUntil ? 'Changes applied' : 'No changes to apply';
    foot.appendChild(el('div', 'op-apply-text', text));
    const discard = btn('Discard', () => this.discard(), 'op-btn op-apply-discard');
    const go = btn('Apply changes', () => this.applyDraft(), 'op-btn op-apply-go');
    discard.dataset.k = 'foot:discard'; go.dataset.k = 'foot:apply';
    discard.disabled = !has; go.disabled = !has;
    foot.append(discard, go);
  }

  /** The rail follows the scroll: the group whose heading last passed the top is "here". */
  private spy(): void {
    if (this.tab !== 'graphics' || !this.body || !this.groups.length) return;
    const top = this.body.scrollTop + 24;
    let here = this.groups[0];
    for (const g of this.groups) if (g.head.offsetTop <= top) here = g;
    if (this.body.scrollTop + this.body.clientHeight >= this.body.scrollHeight - 2) here = this.groups[this.groups.length - 1];
    for (const g of this.groups) g.link.classList.toggle('is-here', g === here);
  }

  // ── Graphics rows: each reads its value TWICE — from the draft (what is shown) and from the live settings (what
  //    "was") — so a row that would change on Apply says so. `when` = an honest note for settings that still wait
  //    for something after Apply (a reload, the next world). ──
  private group(title: string): HTMLDivElement {
    const head = el('div', 'op-group', title);
    const link = btn(title, () => { if (this.body) this.body.scrollTop = head.offsetTop - 6; }, 'op-rail-link');
    link.dataset.k = `rail:${title}`;
    this.body!.appendChild(head);
    this.railEl!.appendChild(link);
    const g = { head, link, changed: 0 };
    this.groups.push(g);
    return head;
  }
  private addRow(row: HTMLElement, changed: boolean): void {
    if (changed) {
      row.classList.add('is-changed');
      this.changedRows++;
      const g = this.groups[this.groups.length - 1];
      if (g) { g.changed++; g.link.dataset.dirty = '1'; }
    }
    this.body!.appendChild(row);
  }
  private gSeg(label: string, values: readonly string[], labels: readonly string[], get: () => string, set: (v: string) => void, when = ''): string {
    const cur = get(), was = inLive(get);
    const wasLabel = labels[values.indexOf(was)] ?? was;
    const row = segRow(label, values, labels, cur, (v) => this.stage(() => set(v)), when, cur !== was ? `was ${wasLabel}` : '');
    this.helpButton(row, label);
    this.addRow(row, cur !== was);
    return cur;
  }
  private gToggle(label: string, get: () => boolean, set: (on: boolean) => void, when = ''): boolean {
    return this.gSeg(label, ['off', 'on'], ['Off', 'On'], () => (get() ? 'on' : 'off'), (v) => set(v === 'on'), when) === 'on';
  }
  private gSlider(label: string, min: number, max: number, step: number, get: () => number, fmt: (v: number) => string, set: (v: number) => void, when = ''): number {
    const cur = get(), was = inLive(get);
    const changed = Math.abs(cur - was) > 1e-9;
    const row = sliderRow(label, min, max, step, cur, fmt, (v) => this.stage(() => set(v)), false, when, changed ? `was ${fmt(was)}` : '');
    this.helpButton(row, label);
    this.addRow(row, changed);
    return cur;
  }

  // ── The "?" (2026-09-29): what a graphics row does, in fair detail, from lib/graphics_help.ts ──
  /** A "?" after the row's label (after the preset buttons on the Preset row) when the help table knows the label. */
  private helpButton(row: HTMLElement, label: string): void {
    const text = graphicsHelp(label);
    if (!text) return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'op-help';
    b.textContent = '?';
    b.dataset.help = label;                 // no data-k: a re-render handing focus back would pop the tooltip unasked
    b.setAttribute('aria-label', `What does ${label} do?`);
    b.setAttribute('aria-expanded', 'false');
    b.addEventListener('mouseenter', () => { if (!this.tipPinned) this.showTip(b, label, text); });
    b.addEventListener('mouseleave', () => { if (!this.tipPinned) this.hideTipSoon(); });
    b.addEventListener('focus', () => { if (!this.tipPinned) this.showTip(b, label, text); });
    b.addEventListener('blur', () => { if (!this.tipPinned) this.hideTipSoon(); });
    b.addEventListener('click', (e) => {
      e.stopPropagation();
      if (this.tipPinned && this.tipFor === b) { this.hideTip(); return; }
      this.showTip(b, label, text);
      this.tipPinned = true;
      b.setAttribute('aria-expanded', 'true');
    });
    const lab = row.querySelector('.op-label');
    if (lab) lab.insertBefore(b, lab.querySelector('.op-when'));
    else row.insertBefore(b, row.querySelector('.op-when, .op-was'));
  }

  private showTip(anchor: HTMLButtonElement, label: string, text: string): void {
    if (!this.overlay) return;
    if (this.tipHideTimer) { clearTimeout(this.tipHideTimer); this.tipHideTimer = null; }
    if (!this.tipEl) {
      const t = el('div', 'op-tip');
      t.id = 'atlas-options-tip';
      t.setAttribute('role', 'tooltip');
      // the tooltip can be hovered (moving onto it to read never closes it — WCAG 1.4.13)
      t.addEventListener('mouseenter', () => { if (this.tipHideTimer) { clearTimeout(this.tipHideTimer); this.tipHideTimer = null; } });
      t.addEventListener('mouseleave', () => { if (!this.tipPinned) this.hideTipSoon(); });
      this.overlay.appendChild(t);
      this.tipEl = t;
    }
    if (this.tipFor && this.tipFor !== anchor) { this.tipFor.setAttribute('aria-expanded', 'false'); this.tipFor.removeAttribute('aria-describedby'); }
    this.tipPinned = false;
    const t = this.tipEl;
    t.replaceChildren(el('div', 'op-tip-title', label), el('div', 'op-tip-text', text));
    t.style.display = 'block';
    anchor.setAttribute('aria-describedby', t.id);
    this.tipFor = anchor;
    // below the "?", its left edge by the "?", kept on screen; above it when there is no room below
    const r = anchor.getBoundingClientRect(), m = 12;
    t.style.left = '0px'; t.style.top = '0px';
    const w = t.offsetWidth, h = t.offsetHeight;
    const x = Math.min(Math.max(m, r.left - 12), window.innerWidth - w - m);
    let y = r.bottom + 8;
    if (y + h > window.innerHeight - m) y = Math.max(m, r.top - 8 - h);
    t.style.left = `${Math.round(x)}px`;
    t.style.top = `${Math.round(y)}px`;
  }

  /** Leaving the "?" hides the tooltip a moment later, so the pointer can cross onto it. */
  private hideTipSoon(): void {
    if (this.tipHideTimer) clearTimeout(this.tipHideTimer);
    this.tipHideTimer = setTimeout(() => { this.tipHideTimer = null; if (!this.tipPinned) this.hideTip(); }, 140);
  }

  private hideTip(): void {
    if (this.tipHideTimer) { clearTimeout(this.tipHideTimer); this.tipHideTimer = null; }
    this.tipPinned = false;
    if (this.tipEl) this.tipEl.style.display = 'none';
    if (this.tipFor) { this.tipFor.setAttribute('aria-expanded', 'false'); this.tipFor.removeAttribute('aria-describedby'); }
    this.tipFor = null;
  }

  private renderGraphics(): void {
    const P = (): ReturnType<typeof activePreset> => activePreset(); // the DRAFT while rendering; the live settings inside inLive()

    // ── Preset ──
    this.group('Preset');
    {
      const cur = `${activeTier()}|${isCustom() ? 'c' : ''}`, was = inLive(() => `${activeTier()}|${isCustom() ? 'c' : ''}`);
      const row = el('div', 'op-row op-row-wide');
      const seg = el('div', 'op-seg');
      for (const t of TIER_ORDER) {
        const b = btn(TIER_LABEL[t], () => this.stage(() => setActiveTier(t)));
        b.dataset.k = `Preset|${t}`;
        if (activeTier() === t) b.classList.add(isCustom() ? 'base' : 'on'); // customised: the preset it started from is underlined, not filled
        seg.appendChild(b);
      }
      row.appendChild(seg);
      const note = isCustom() ? `${TIER_LABEL[activeTier()]}, customised` : '';
      if (note) row.appendChild(el('span', 'op-when', note));
      const tierChanged = cur.split('|')[0] !== was.split('|')[0];
      if (tierChanged) row.appendChild(el('span', 'op-was', `was ${inLive(() => TIER_LABEL[activeTier()])}`));
      this.helpButton(row, 'Preset');
      this.addRow(row, tierChanged);
      this.body!.appendChild(noteRow('A preset sets every row below. Change any row afterwards and the preset becomes customised.'));
    }
    this.gToggle('Auto-tune', () => P().autoTune ?? false, setAutoTune, 'moves the preset with your frame rate');

    // ── Resolution ──
    this.group('Resolution');
    // Resolution TARGET (2026-09-08): the canvas height whatever the display — minimal 480p … ultra 4K; Native = the ratio slider below.
    this.gSeg('Resolution', RESOLUTION_TARGETS.map((t) => String(t.height)), RESOLUTION_TARGETS.map((t) => t.label), () => String(P().targetHeight ?? 0), (v) => setTargetHeight(Number(v)));
    this.gSlider('Render scale', MIN_RENDER_SCALE, 2, 0.05, () => P().pixelRatio, (v) => `${v.toFixed(2)}×`, setRenderScale);
    this.gSeg('Upscaling', ['off', 'ultra', 'quality', 'balanced', 'performance'], ['Off', 'Ultra 1.3×', 'Quality 1.5×', 'Balanced 1.7×', 'Perf 2×'], () => P().upscale ?? 'off', (v) => setUpscale(v as UpscaleMode));
    this.gSeg('Upscaler', ['fsr', 'taau'], ['FSR 1, spatial', 'TAAU, temporal'], () => P().upscaleMode ?? 'fsr', (v) => setUpscaleMode(v as 'fsr' | 'taau'));
    this.gToggle('Adaptive render scale', () => P().adaptive ?? false, setAdaptive);
    const aa = this.gSeg('Anti-aliasing', ['off', 'fxaa', 'smaa', 'traa'], ['Off', 'FXAA', 'SMAA', 'TRAA'], () => currentAA(), (m) => setAA(m as AAMode));
    if (aa !== 'off') this.gSeg('Anti-aliasing quality', ['2', '4', '8', '16', '32'], ['2×', '4×', '8×', '16×', '32×'], () => String(currentAAQuality()), (v) => setAAQuality(Number(v)));
    // R3 (2026-09-25): AMD's order on the spatial upscaler — tone map + AA at render resolution, THEN EASU.
    this.gToggle('Anti-alias before upscaling', () => currentAaFirst(), setAaFirst, 'FSR 1 tiers');
    this.gSeg('Upscale kernel', ['amd', 'legacy'], ['AMD EASU', 'Legacy'], () => currentEasuKernel(), (v) => setEasuKernel(v as 'amd' | 'legacy'));
    this.gToggle('Sharper textures (mip bias)', () => P().mipBias === true, setMipBias, 'follows the render scale');
    this.gSeg('Texture quality', ['512', '1024', '2048', '4096'], ['¼', '½', 'Full', 'Max'], () => String(P().maxTextureRes), (v) => setMaxTextureRes(Number(v)));
    this.gSeg('Anisotropy', ['1', '2', '4', '8', '16'], ['1×', '2×', '4×', '8×', '16×'], () => String(P().anisotropy ?? 4), (v) => setAnisotropy(Number(v)));
    // R4 (2026-09-25): shown and set as the HORIZONTAL angle at 16:9, stored vertical (a fixed vertical FOV is Hor+).
    this.gSlider('Field of view', 80, 120, 1, () => hFovFromV((P().cameraFov ?? 0) > 0 ? P().cameraFov! : FIRST_PERSON_VFOV, REFERENCE_ASPECT), (v) => `${Math.round(v)}° horizontal`, (h) => setCameraFov(vFovFromH(h, REFERENCE_ASPECT)));

    // ── Light and shadow ──
    this.group('Light and shadow');
    this.gSeg('Shadows', ['off', 'low', 'med', 'high', 'ultra'], ['Off', 'Low', 'Med', 'High', 'Ultra'], () => currentShadows(), (m) => setShadows(m as ShadowLevel));
    this.gToggle('Capsule shadows', () => currentCapsuleShadows(), setCapsuleShadows);
    this.gToggle('Contact shadows', () => !!P().post.contactShadows, setContactShadows);
    this.gToggle('Cloud shadows', () => P().cloudShadows === true, setCloudShadows);
    if (this.gToggle('Ambient occlusion', () => !!P().post.ao, setSSAO)) {
      this.gSlider('Ambient occlusion intensity', 0, 3, 0.1, () => P().post.ao?.intensity ?? 1, (v) => v.toFixed(1), setAoIntensity);
      this.gSlider('Ambient occlusion radius', 0.1, 2, 0.05, () => P().post.ao?.radius ?? 0.5, (v) => `${v.toFixed(2)} m`, setAoRadius);
    }
    this.gToggle('Global illumination (SSGI)', () => !!P().post.ssgi, setSsgi);
    this.gToggle('Screen-space reflections', () => !!P().post.ssr, setSsr); // both backends since the gaps wave round 2 (the note said 'WebGPU only')
    if (this.gToggle('Volumetric light shafts', () => P().volumetricLights === true, setVolumetricLights)) { // shader gaps R3 (agent F): local volumes
      this.gSeg('Light shaft resolution', ['half', 'full'], ['Half', 'Full'], () => P().volumeResolution ?? 'half', (v) => setVolumeResolution(v === 'full' ? 'full' : 'half'), 'live');
      this.gSeg('Light shaft noise', ['golden', 'stbn', 'white'], ['Golden', 'STBN', 'White'], () => P().volumeNoise ?? 'golden', (v) => setVolumeNoise(v === 'stbn' || v === 'white' ? v : 'golden'), 'live');
    }
    this.gToggle('Window area lights at night', () => P().areaLights === true, setAreaLights, 'after a reload');
    this.gToggle('Window light shadows', () => P().windowShadows === true, setWindowShadows, 'after a reload'); // shader gaps R5
    this.gToggle('Lamp light cookies', () => P().lampCookies === true, setLampCookies, 'after a reload');

    // ── World detail ──
    this.group('World detail');
    this.gSlider('Draw distance', MIN_DRAW_DISTANCE_M, 5000, 100, () => currentDrawDistance(), (v) => `${(v / 1000).toFixed(1)} km`, setDrawDistance);
    // Distant detail (2026-09-08): everything past the draw distance — HLOD settlements, silhouettes, the far terrain — or a fog-closed void.
    this.gToggle('Distant detail', () => P().distantDetail !== false, setDistantDetail, 'far towns and terrain past the draw distance');
    this.gSlider('Foliage density', 0, 1.5, 0.05, () => P().foliageDensityScale, (v) => `${Math.round(v * 100)}%`, setFoliageDensity);
    this.gSlider('Level-of-detail distance', 0.5, 6, 0.25, () => P().lodDistanceScale, (v) => `${v.toFixed(2)}×`, setLodDistanceScale);
    this.gSlider('Settlement detail', 0.5, 3, 0.1, () => P().settlementRadiusScale, (v) => `${v.toFixed(1)}×`, setSettlementDetail);
    this.gSeg('Settlement build', ['1', '2'], ['Detailed', 'Simplified'], () => String(P().settlementBuildLod ?? 1), (v) => setSettlementBuildLod(Number(v) as 1 | 2));
    this.gSeg('Placement detail', ['0', '1', '2', '3'], ['Full', '½', '¼', 'Min'], () => String(P().placementLod ?? 0), (v) => setPlacementLod(Number(v) as 0 | 1 | 2 | 3));
    this.gSlider('Particle density', 0, 1, 0.05, () => P().particleCap, (v) => `${Math.round(v * 100)}%`, setParticleDensity);
    this.gToggle('Model smoothing', () => P().meshSmoothing, setModelSmoothing);
    this.gToggle('Surface detail', () => P().surfaceDetail, setSurfaceDetail);
    this.gSeg('Ground shader', ['full', 'simple'], ['Full', 'Simple'], () => P().groundQuality ?? 'full', (v) => setGroundQuality(v as 'full' | 'simple'), 'after a reload');
    this.gToggle('Ground anti-tiling', () => P().groundAntiTiling === true, setGroundAntiTiling);
    // Ground material v2 (2026-09-25; research §7 R1/R7/R8): rebuilds the ground live (a v1 ↔ v2 flip rebuilds the chunks).
    this.gSeg('Ground material', ['v2', 'v1'], ['Scanned (v2)', 'Classic (v1)'], () => groundKnobs().material, (v) => setGroundMaterial(v as 'v1' | 'v2'));
    // ground (2026-09-26): the 4K / 8K rungs (the owner's ask); a device that cannot hold one falls down the ladder.
    this.gSeg('Ground texture detail', ['512', '1024', '2048', '4096', '8192'], ['512', '1K', '2K', '4K', '8K'], () => String(groundKnobs().res), (v) => setGroundArrayRes(Number(v) as 512 | 1024 | 2048 | 4096 | 8192));
    this.gToggle('Ground triplanar rock', () => groundKnobs().triplanar, setGroundTriplanar);
    this.gSeg('Ground parallax', ['off', 'rock', 'all'], ['Off', 'Rock', 'All near'], () => groundKnobs().pom, (v) => setGroundPom(v as 'off' | 'rock' | 'all'));
    // surfaces (2026-09-27): §2.1 micro-shadowing (default on) · §2.3 hex-tiling (high / ultra, rides anti-tiling) · §2.4 the parallax self-shadow (off by default, rides parallax)
    this.gToggle('Ground micro-shadowing', () => groundKnobs().microShadow, setGroundMicroShadow); this.gToggle('Ground hex-tiling', () => groundKnobs().hexTiling, setGroundHexTiling, 'with anti-tiling'); this.gToggle('Parallax self-shadow', () => groundKnobs().pomShadow, setGroundPomShadow, 'with ground parallax');
    // ONE row for the knob (2026-09-25 session 2: a second 'Near terrain mesh' row drove the same setter; the ring re-LODs on apply now).
    this.gSeg('Near ground mesh', ['48', '96'], ['Standard (5 m)', 'Dense (2.7 m, +124k tris)'], () => String(groundKnobs().nearSeg), (v) => setGroundNearSeg(Number(v) === 96 ? 96 : 48));
    this.gToggle('Far-tree frame blending', () => P().impostorBlend === true, setImpostorBlend, 'at the next tree bake');
    // Vegetation realism (2026-09-25): grass blades near, clump cards beyond; rock where the ground is steep; the understory; woods to the horizon.
    this.gSeg('Grass', ['off', 'cards', 'low', 'med', 'high', 'ultra'], ['Off', 'Cards', 'Low', 'Med', 'High', 'Ultra'], () => P().grassLayer ?? 'off', (v) => setGrassLayer(v as TierPreset['grassLayer']));
    this.gSlider('Rocks and cliffs', 0, 1.5, 0.05, () => P().geologyDensity ?? 0, (v) => `${Math.round(v * 100)}%`, setGeologyDensity);
    this.gSlider('Undergrowth', 0, 1.5, 0.05, () => P().understoryDensity ?? 0, (v) => `${Math.round(v * 100)}%`, setUnderstoryDensity);
    this.gSlider('Forest horizon', 0, 12000, 500, () => P().forestHorizonM ?? 0, (v) => (v <= 0 ? 'Off' : `${(v / 1000).toFixed(1)} km`), setForestHorizon);
    this.gToggle('Woods on the far terrain', () => P().farCanopy === true, setFarCanopy, 'at the next far-terrain rebuild');
    this.gToggle('Plant colour variation', () => P().foliageJitter === true, setFoliageJitter, 'as plants stream in');
    // R12 "Ambient life" (gaps wave round 2, 2026-09-28): the row is back because the flag has readers now —
    // AtlasAmbientLifeModule (falling leaves, pollen motes, dust devils) and the fauna's insect swarm. Sized by the tier's particle budget.
    this.gToggle('Ambient life', () => P().ambientLife !== false, setAmbientLife, 'leaves, pollen, dust devils, insects, grazing herds, butterflies, dragonflies, crabs — live');
    // Shader gaps (2026-09-29): the caves' baked sky transfer + crystal light (lib/cave_light*.ts) — live.
    this.gSeg('Cave light', ['off', 'visibility', 'baked', 'baked+probe'], ['Off', 'Sky only', 'Baked', 'Baked + probe'], () => P().caveLight ?? 'baked+probe', (v) => setCaveLight(v as 'off' | 'visibility' | 'baked' | 'baked+probe'), 'live');
    this.gSeg('Material damage', ['off', 'marks', 'full'], ['Off', 'Marks', 'Full'], () => P().materialDamage ?? 'marks', (v) => setMaterialDamage(v as 'off' | 'marks' | 'full'), 'as buildings load'); // shader gaps R2
    // shader gaps R4 (material models): EON on rough rock, the Laplacian ground transition, the slate's mica flakes
    this.gToggle('Rough stone diffuse', () => P().eonDiffuse === true, setEonDiffuse, 'after a reload');
    this.gToggle('Ground transition blend', () => P().laplacianBlend === true, setLaplacianBlend, 'after a reload');
    this.gToggle('Mineral glints', () => P().flakeGlints === true, setFlakeGlints, 'after a reload');
    this.gToggle('Dual-quaternion skinning', () => P().dqSkinning === true, setDqSkinning);
    this.gToggle('Skin shading', () => P().skinShading === true, setSkinShading, 'live'); // shader gaps R2
    // R8 (2026-09-25): the character layers.
    this.gToggle('Animated player bodies', () => P().playerBodyRig === true, setPlayerBodyRig, 'as bodies are rebuilt');
    this.gToggle('Foot IK', () => P().footIk === true, setFootIk, 'feet on slopes and steps');
    this.gToggle('Breathing', () => P().breathing === true, setBreathing, 'idle characters');

    // ── Sky and weather ──
    this.group('Sky and weather');
    this.gSeg('Sky', ['full', 'flat'], ['Atmosphere', 'Flat'], () => P().skyQuality ?? 'full', (v) => setSkyQuality(v as 'full' | 'flat'), 'after a reload');
    if (this.gToggle('Volumetric clouds', () => P().cloudsEnabled, setClouds)) {
      // Second low cloud layer (~800-1500 m puffs) — only meaningful when the volumetric clouds themselves are on.
      this.gToggle('Low clouds', () => currentLowClouds(), setLowClouds);
      // The high étage (2026-09-25): cirrus / cirrostratus as a plane-projected sheet at 8.5 km.
      this.gToggle('High cirrus layer', () => currentHighClouds(), setHighClouds, 'next world entry');
      // 0 = frozen to the camera; 1 (default) = full world parallax — clouds stationary in the world.
      this.gSlider('Cloud parallax', 0, 1, 0.05, () => currentCloudParallax(), (v) => (v === 0 ? 'Frozen' : v.toFixed(2)), setCloudParallax);
      this.gSlider('Cloud resolution', 0.25, 1, 0.25, () => P().cloudResolution ?? 1, (v) => `${Math.round(v * 100)}%`, setCloudResolution);
      this.gSeg('Cloud march quality', ['auto', 'low', 'medium', 'high'], ['Auto', 'Low', 'Med', 'High'], () => P().cloudMarchQuality ?? 'auto', (v) => setCloudMarchQuality(v as 'auto' | 'low' | 'medium' | 'high'));
    }
    // Distance fog: toggle + distance (metres to the ~63% extinction depth).
    if (this.gToggle('Distance fog', () => currentFogDistance() > 0, (on) => setFogDistance(on ? 1200 : 0))) {
      this.gSlider('Fog distance', 400, 5000, 100, () => currentFogDistance(), (v) => `${v} m`, setFogDistance);
    }
    if (this.gToggle('Volumetric fog', () => currentVolumetricFogPass(), setVolumetricFogPass)) {
      this.gSlider('Volumetric fog distance', 60, 600, 20, () => currentVolFogDistance(), (v) => `${v} m`, setVolFogDistance);
    }
    // Aerial haze needs the sky's froxel bake, which only starts when the page boots with it on.
    this.gToggle('Aerial haze', () => currentAerialPerspective(), setAerialPerspective, 'after a reload');
    this.gToggle('Aurora nights', () => P().aurora === true, setAurora);
    this.gToggle('Rain ripples', () => P().rainRipples === true, setRainRipples, 'after a reload');
    this.gToggle('Rain shelter', () => P().rainShelter === true, setRainShelter, 'roofs stop rain and keep the ground dry');
    this.gSeg('Wet surfaces', ['off', 'ground', 'all'], ['Off', 'Ground', 'Everything'], () => P().wetSurfaces ?? 'ground', (v) => setWetSurfaces(v as 'off' | 'ground' | 'all'), 'props as they load'); // shader gaps R3
    this.gToggle('Snow and mud tracks', () => P().surfaceDeform === true, setSurfaceDeform, 'needs Rain shelter · at the ground\'s next build'); // shader gaps R3
    this.gSeg('Rain puddles', ['off', 'small', 'medium', 'large'], ['Off', '64 m', '96 m', '128 m'], () => P().puddleSim ?? 'off', (v) => setPuddleSim(v as 'off' | 'small' | 'medium' | 'large'), 'WebGPU · at the ground\'s next build'); // shader gaps R6
    this.gToggle('Nebula volumes', () => P().nebulae === true, setNebulae, 'in space');
    this.gToggle('Planet atmospheres', () => P().planetAtmosphere === true, setPlanetAtmosphere, 'in space');
    // The space realism wave (2026-09-25, the space agent): each defaults per tier (SPACE_TIER_FLAGS).
    this.gToggle('Space sky (Milky Way + catalogue stars)', () => P().spaceSky !== false, setSpaceSky, 'in space · next entry');
    this.gToggle('Space speed cues (dust, streaks, FOV)', () => P().spaceMotion !== false, setSpaceMotion, 'in space');
    this.gToggle('Engine plumes', () => P().spacePlumes === true, setSpacePlumes, 'in space');
    this.gToggle('Lit war hulls + nav lights', () => P().spaceLitHulls === true, setSpaceLitHulls, 'in space · next entry');
    this.gToggle('Shield ripples + hull damage', () => P().spaceShields === true, setSpaceShields, 'in space');
    // shader gaps R4 (space light): receiver-side eclipses, rings, the Commons' orbital cloud shell — each defaults per tier
    this.gToggle('Eclipse shadows', () => P().eclipseShadows === true, setEclipseShadows, 'in space · next entry');
    this.gToggle('Planetary rings', () => P().planetRings === true, setPlanetRings, 'in space · next entry');
    this.gToggle('Orbital clouds', () => P().orbitalClouds === true, setOrbitalClouds, 'in space · next entry');
    this.gSeg('Black hole lensing', ['off', 'low', 'high'], ['Off', 'Low', 'High'], () => P().blackHole ?? 'high', (v) => setBlackHole(v === 'off' || v === 'low' ? v : 'high'), 'in space · live'); // shader gaps: black hole

    // ── Water ──
    this.group('Water');
    this.gSeg('River water', ['realistic', 'simple'], ['Realistic', 'Simple'], () => P().riverWater ?? 'realistic', (v) => setRiverWater(v as 'realistic' | 'simple'));
    this.gToggle('Water reflections', () => P().waterReflections ?? false, setWaterReflections, 'at the next world');
    this.gToggle('Realistic fluid physics', () => P().fluidPhysics !== false, setFluidPhysics, 'wakes, wading, the wind on the sea');
    this.gToggle('Sea foam trails', () => P().waterFoam === true, setWaterFoam, 'after a reload');
    this.gToggle('Projected caustics', () => P().waterCaustics === true, setWaterCaustics, 'after a reload');
    this.gToggle('FFT ocean', () => P().fftOcean === true, setFftOcean, 'ultra · WebGPU · the sea rebuilds'); // shader gaps R5
    this.gToggle('Underwater light shafts', () => P().underwaterShafts === true, setUnderwaterShafts, 'after a reload');
    // Water realism wave (2026-09-25): the look switches (applying one rebuilds the water bodies).
    for (const [k, label, note] of WATER_LOOK_ROWS) this.gToggle(label, () => (P() as unknown as Record<string, unknown>)[k] === true, (on: boolean) => setWaterLook(k, on), note);

    // ── Materials ──
    this.group('Materials');
    this.gToggle('Foliage translucency', () => P().foliageShading !== false, setFoliageShading, 'leaves lit from behind');
    // Wind has its OWN switch (2026-09-14) so a custom profile on a cheap tier can still have the trees move.
    this.gToggle('Tree and foliage wind', () => currentFoliageWind(), setFoliageWind);
    this.gToggle('Material effects', () => P().materialFx !== false, setMaterialFx, 'snow, clearcoat');
    // R6 (2026-09-25): macro grime + the finish's crevices; the detail surface on props.
    this.gToggle('Grime and weathering', () => P().materialGrime === true, setMaterialGrime, 'crevices, ledges');
    this.gToggle('Surface detail on props', () => P().propSurfaceDetail ?? P().surfaceDetail, setPropSurfaceDetail, 'newly loaded props');
    this.gToggle('Interior mapping', () => P().interiorMapping === true, setInteriorMapping, 'rooms behind windows');
    this.gSeg('Interior detail', ['0', '1', '2'], ['Essential', 'Standard', 'Full'], () => String(P().interiorDetail ?? 1), (v) => setInteriorDetail((v === '0' ? 0 : v === '2' ? 2 : 1) as 0 | 1 | 2), 'at the next room you enter'); // interiors programme, slice D
    this.gToggle('Effect shaders', () => P().vfxShaders === true, setVfxShaders, 'haze, shields, dissolve, holograms');
    this.gToggle('Lit impact decals', () => P().litDecals === true, setLitDecals);
    this.gToggle('Specular anti-aliasing', () => P().specularAA === true, setSpecularAA, 'normal-map roughness filter');
    this.gToggle('Procedural detail filtering', () => P().proceduralFiltering === true, setProceduralFiltering, 'no shimmer at distance');
    this.gToggle('Lit smoke and dust', () => P().litSmoke === true, setLitSmoke);
    // Fire realism (2026-09-25): the fire shader family's rung, its heat haze and its share of the light pool.
    this.gSeg('Fire', ['sprite', 'cards', 'volume', 'sim'], ['Sprites', 'Cards', 'Volume', 'Simulated'], () => P().fireQuality ?? 'volume', (v) => setFireQuality(v as 'sprite' | 'cards' | 'volume' | 'sim'), 'simulated needs WebGPU');
    this.gToggle('Heat haze over fires', () => P().fireHaze === true, setFireHaze);
    this.gSeg('Fire lights', ['1', '2', '3'], ['1', '2', '3'], () => String(P().fireLights ?? 2), (v) => setFireLights(Number(v)), 'after a reload');
    this.gToggle('Glass transmission', () => P().glassTransmission === true, setGlassTransmission, 'props, canopies');
    this.gToggle('Brushed-metal anisotropy', () => P().hardSurfaceAnisotropy === true, setHardSurfaceAnisotropy);
    this.gToggle('Retroreflective signs and markers', () => P().retroreflection === true, setRetroreflection);

    // ── Camera and lens ──
    this.group('Camera and lens');
    if (this.gToggle('Bloom', () => !!P().post.bloom, setBloom)) {
      // 2026-09-25: Kawase = the energy-conserving veil (threshold-less, a few % mix); Gaussian = the legacy additive glow.
      this.gSeg('Bloom filter', ['kawase', 'mip'], ['Kawase veil (physical)', 'Gaussian glow (legacy)'], () => currentBloomMode(), (v) => setBloomMode(v as 'mip' | 'kawase'));
      const veil = P().post.bloom?.composite === 'lerp';
      if (veil) this.gSlider('Bloom veil', 0, 0.12, 0.005, () => currentBloomStrength(), (v) => `${(v * 100).toFixed(1)} %`, setBloomStrength);
      else {
        this.gSlider('Bloom intensity', 0, 1.5, 0.05, () => currentBloomStrength(), (v) => v.toFixed(2), setBloomStrength);
        this.gSlider('Bloom threshold', 0.4, 1.6, 0.05, () => P().post.bloom?.threshold ?? 1.1, (v) => v.toFixed(2), setBloomThreshold);
      }
      this.gSlider('Bloom radius', 0, 1, 0.05, () => P().post.bloom?.radius ?? 0.6, (v) => v.toFixed(2), setBloomRadius);
    }
    // R4 (2026-09-25): the ONE camera model's lens — third person autofocuses on you, first person is always sharp.
    this.gSeg('Depth of field', ['off', 'thirdPerson'], ['Off', 'Third person'], () => P().cameraDof ?? 'off', (v) => setCameraDof(v as 'off' | 'thirdPerson'));
    this.gSeg('Aperture', ['2.8', '4', '5.6', '8', '11'], ['f/2.8', 'f/4', 'f/5.6', 'f/8', 'f/11'], () => String(P().cameraFNumber ?? 5.6), (v) => setCameraFNumber(Number(v)));
    if (this.gToggle('Depth of field (manual)', () => !!P().post.dof, setDoF)) {
      this.gSeg('Bokeh style', ['standard', 'hex'], ['Standard', 'Hex'], () => currentBokehStyle(), (m) => setBokehStyle(m as 'standard' | 'hex'));
      this.gSlider('Focus distance', 2, 4000, 5, () => currentDofFocusDistance(), (v) => (v >= 1000 ? `${(v / 1000).toFixed(2)} km` : `${v} m`), setDofFocusDistance);
      // Metres of focus RANGE, not a lens length (2026-09-14) — the old 0.01–0.2 slider blurred the entire world at every position on it.
      this.gSlider('Focus range', 4, 400, 1, () => currentDofFocalLength(), (v) => `${Math.round(v)} m`, setDofFocalLength);
      this.gSlider('Bokeh scale', 0.3, 3, 0.1, () => currentDofBokehScale(), (v) => `${v.toFixed(1)}×`, setDofBokehScale);
    }
    if (this.gToggle('Motion blur', () => currentMotionBlur(), setMotionBlur)) {
      this.gSlider('Motion blur intensity', 0.25, 3, 0.25, () => currentMotionBlurIntensity(), (v) => `${v.toFixed(2)}×`, setMotionBlurIntensity);
      this.gSlider('Motion blur samples', 4, 32, 4, () => P().post.motionBlur?.samples ?? 12, (v) => String(v), setMotionBlurSamples);
    }
    this.gToggle('Lens flares', () => !!(P().post as { lensFlares?: unknown }).lensFlares, setLensFlares);
    this.gToggle('God rays', () => !!P().post.godRays, setGodRays);
    if (this.gToggle('Vignette', () => currentVignette(), setVignette)) {
      this.gSlider('Vignette strength', 0, 1, 0.05, () => P().post.vignette?.strength ?? 0.4, (v) => v.toFixed(2), setVignetteStrength);
    }
    if (this.gToggle('Film grain', () => currentFilmGrain(), setFilmGrain)) {
      // 'luma' grain (2026-09-25) lives at 1–2 %; the legacy three film grain took 0..1.
      if (P().post.film?.mode === 'luma') this.gSlider('Film grain intensity', 0, 0.05, 0.0025, () => P().post.film?.intensity ?? 0.015, (v) => `${(v * 100).toFixed(1)} %`, setFilmGrainIntensity);
      else this.gSlider('Film grain intensity', 0, 1, 0.05, () => P().post.film?.intensity ?? 0.35, (v) => v.toFixed(2), setFilmGrainIntensity);
    }
    if (this.gToggle('Chromatic aberration', () => currentChromaticAberration(), setChromaticAberration)) {
      this.gSlider('Chromatic aberration strength', 0, 2, 0.05, () => P().post.chromaticAberration?.strength ?? 0.6, (v) => v.toFixed(2), setChromaticStrength);
    }

    // ── Colour ──
    this.group('Colour');
    // 2026-09-25 (§8 R3/R4, §10 R2): AgX is the default operator; 'ACES 2' is the engine's hue-preserving node.
    this.gSeg('Tone mapping', ['agx', 'aces2', 'aces', 'neutral', 'reinhard'], ['AgX', 'ACES 2', 'ACES 1', 'Neutral', 'Reinhard'], () => P().tonemapOperator ?? 'agx', (v) => setTonemapOperator(v as 'aces' | 'agx' | 'aces2' | 'neutral' | 'reinhard'));
    const expMode = this.gSeg('Exposure', ['physical', 'fixed'], ['Time of day (physical)', 'Fixed camera'], () => currentExposureMode(), (v) => setExposureMode(v as 'physical' | 'fixed'));
    if (expMode === 'fixed') this.gSlider('Fixed exposure', 0.4, 1.8, 0.05, () => P().tonemapExposure ?? 0.85, (v) => v.toFixed(2), setTonemapExposure);
    else this.gSlider('Exposure compensation', 0.4, 1.8, 0.05, () => P().tonemapExposure ?? 0.85, (v) => { const ev = Math.log2(v / 0.85); return `${ev >= 0 ? '+' : ''}${ev.toFixed(1)} EV`; }, setTonemapExposure);
    if (expMode !== 'fixed' && this.gToggle('Auto exposure', () => currentAutoExposure(), setAutoExposure)) {
      this.gSlider('Auto exposure strength', 0, 1, 0.05, () => currentAutoExposureStrength(), (v) => `${Math.round(v * 100)}%`, setAutoExposureStrength);
      this.gSeg('Exposure metering', ['average', 'histogram'], ['Log average', 'Histogram, trimmed'], () => currentAutoExposureMetering(), (m) => setAutoExposureMetering(m as 'average' | 'histogram'));
    }
    this.gSeg('Look', LOOKS, LOOKS.map((l) => LOOK_LABEL[l]), () => P().look ?? 'none', (v) => setLook(v as LookName));
    this.gSlider('Saturation', 0.5, 1.5, 0.05, () => currentSaturation(), (v) => `${Math.round(v * 100)}%`, setSaturation);
    this.gSlider('Sharpen', 0, 0.6, 0.05, () => currentSharpen(), (v) => (v === 0 ? 'Off' : v.toFixed(2)), setSharpen);
    const cb = this.gSeg('Colourblind correction', ['off', 'protanopia', 'deuteranopia', 'tritanopia'], ['Off', 'Protanopia', 'Deuteranopia', 'Tritanopia'], () => P().colorblindMode ?? 'off', (v) => setColorblindMode(v as 'off' | 'protanopia' | 'deuteranopia' | 'tritanopia'));
    if (cb !== 'off') this.gSlider('Correction strength', 0, 1, 0.05, () => P().colorblindStrength ?? 1, (v) => `${Math.round(v * 100)}%`, setColorblindStrength);
  }

  // ── Sound ── (the shared audio runtime, 2026-09-20; versona voices: KittenTTS in the browser, 2026-09-10)
  // Every row writes the runtime (lib/audio/runtime.ts), which persists it and ramps the live graph — the menu
  // holds no audio state of its own. Sound changes AS YOU SET IT: a level is judged by ear, not by a button.
  private renderSound(): void {
    const tts = versonaTts();
    const rt = audioRuntime();
    const pct = (v: number): string => `${Math.round(v * 100)}%`;
    const cols = el('div', 'op-cols');
    const left = el('div'), right = el('div');
    left.appendChild(el('div', 'op-group', 'Levels'));
    left.appendChild(toggleRow('Mute all game audio', rt.isMuted(), (on) => { rt.setMuted(on); this.render(); }));
    for (const g of VOLUME_GROUPS) {
      const m = /^(.*?)\s*\((.*)\)\s*$/.exec(VOLUME_LABEL[g]);   // "Effects (combat, vehicles, …)" → a label and its note
      const name = m ? m[1] : VOLUME_LABEL[g], what = m ? m[2] : '';
      // Dialogue keeps its older control: the TTS service owns that gain (one owner — never applied twice).
      if (g === 'dialogue') left.appendChild(sliderRow(name, 0, 1, 0.05, tts.volume(), pct, (v) => { tts.setVolume(v); rt.setVolume('dialogue', v); /* same key, same value: keeps __audio() honest; the bus gain stays 1 */ }, true, what));
      else left.appendChild(sliderRow(name, 0, 1, 0.05, rt.volume(g), pct, (v) => { rt.setVolume(g, v); }, true, what));
    }
    right.appendChild(el('div', 'op-group', 'Output'));
    right.appendChild(segRow('Output', [...OUTPUT_MODES], ['Headphones', 'Speakers', 'Mono'], rt.outputMode(), (v) => { rt.setOutputMode(v as OutputMode); this.render(); }));
    right.appendChild(noteRow(rt.outputMode() === 'headphones'
      ? 'Headphones: the sounds you need to locate (threats, evidence, speech) are placed around you — above, behind, beside.'
      : rt.outputMode() === 'speakers' ? 'Speakers: plain stereo panning. Placement around the head smears over loudspeakers.'
      : 'Mono: both ears carry everything — for single-sided hearing, one earbud or a phone speaker. Turn on sound captions for direction.'));
    right.appendChild(segRow('Dynamic range', ['full', 'night'], ['Full', 'Night'], rt.isNightMode() ? 'night' : 'full', (v) => { rt.setNightMode(v === 'night'); this.render(); }));
    right.appendChild(toggleRow('Room acoustics', rt.isReverbEnabled(), (on) => { rt.setReverbEnabled(on); this.render(); }, 'caves, halls, interiors'));
    right.appendChild(toggleRow('Sound captions', rt.captionsEnabled(), (on) => { rt.setCaptionsEnabled(on); this.render(); }, 'what you heard, and which way'));
    {
      const row = labeledRow('Speaker check');
      const seg = el('div', 'op-seg');
      for (const [label, where] of [['Left', 'left'], ['Ahead', 'ahead'], ['Right', 'right']] as const) {
        const b = btn(label, () => { rt.playTest(where); });
        b.dataset.k = `Speaker check|${where}`;
        seg.appendChild(b);
      }
      row.lastElementChild!.appendChild(seg);
      right.appendChild(row);
    }
    right.appendChild(el('div', 'op-group', 'Voices'));
    right.appendChild(toggleRow('Versona voices', tts.enabled(), (on) => { tts.setEnabled(on); this.render(); }, 'spoken dialogue'));
    right.appendChild(toggleRow('Overheard chatter', tts.ambientEnabled(), (on) => {
      try { localStorage.setItem('atlas:voices-ambient', on ? 'on' : 'off'); } catch { /* storage denied */ }
      if (!on) { try { if (tts.liveRank() === 1) tts.stop(); } catch { /* not built */ } }
      this.render();
    }, 'one voice at a time, within earshot'));
    cols.append(left, right);
    this.body!.appendChild(cols);
  }

  // ── Controls ── keyboard (rebindable, 2026-09-16) beside the gamepad (gamepad everywhere, 2026-09-16)
  private renderControls(): void {
    const body = this.body!;
    const cols = el('div', 'op-cols');
    const kb = el('div'), pad = el('div');
    kb.dataset.bind = 'key'; pad.dataset.bind = 'pad';

    // ── Camera (physics follow-up A, 2026-10-05; XAG 117): ONE scale for every ADDITIVE camera motion on foot and in a
    //    vehicle (lib/camera_comfort.ts) — the kicks, the shake, the sprint / dash FOV, the landing dip, the chase lag
    //    and bank. A comfort setting is judged by feel, so it applies LIVE (no Apply), like the sector's row below.
    {
      kb.appendChild(el('div', 'op-group', 'Camera'));
      const cmRow = segRow('Camera motion', CAMERA_MOTIONS, ['System', 'Full', 'Reduced', 'Off'], cameraMotion(), (v) => { setCameraMotion(v as CameraMotion); this.render(); });
      cmRow.dataset.k = 'camera:motion';
      kb.appendChild(cmRow);
      // The shake stays OPT-IN (XAG 117: avoid it or make it optional) — the old `atlas:feel-shake` flag, now with a row.
      const shakeRow = toggleRow('Camera shake (hits, crashes, lightning)', shakeOptIn(), (on) => { setShakeOptIn(on); feel.shakeEnabled = on; this.render(); });
      shakeRow.dataset.k = 'camera:shake';
      kb.appendChild(shakeRow);
      kb.appendChild(noteRow('System follows your device\'s "reduce motion" setting. Reduced keeps a third of the camera kicks, the shake, the sprint and dash field-of-view widening and the hovercraft and ship chase cameras\' lag and bank; Off removes them. Where your eye is (crouching, sliding, sitting) and your aim are never changed. A headset never moves the view. Both apply at once.'));
    }

    // ── Flight (THE AERO LAW, 2026-09-22): the sector flies like the sky ──
    {
      kb.appendChild(el('div', 'op-group', 'Flight'));
      const aeroRow = toggleRow('Fly the sector like the sky', flightModel() === 'aero', (on) => { setFlightModel(on ? 'aero' : 'space'); this.render(); });
      aeroRow.dataset.k = 'flight:aero';
      kb.appendChild(aeroRow);
      kb.appendChild(noteRow('On: the same stick as atmospheric flight — the mouse turns every hull at the sky\'s rate, the hull banks into its turns, turning is sharpest near half throttle and a hard turn bleeds speed. Off: the old sector model (a chassis-scaled mouse, no bank). Applies at once.'));
      // space campaign B (XAG 107: a toggle for a non-competitive hold): the utility beam mines / salvages / recovers
      // while held — or, toggled, until pressed again. The server meters the same work either way.
      const beamRow = segRow('Utility beam', ['hold', 'toggle'], ['Hold', 'Toggle'], beamMode(), (v) => { setBeamMode(v === 'toggle' ? 'toggle' : 'hold'); this.render(); });
      beamRow.dataset.k = 'flight:beam';
      kb.appendChild(beamRow);
      // space campaign L (2026-10-01, report §22.2 / XAG 117): the sector's SECONDARY motion (shake, speed streaks, the
      // FOV kick, dust, the warp tunnel's spin, your own warp flash) — never the aim, damage or threat data — and the
      // sector HUD's own size. A headset has its own choices on the VR Comfort page.
      const sc = desktopSpaceComfort();
      const motionRow = segRow('Secondary motion in the sector', ['system', 'full', 'reduced', 'off'], ['System', 'Full', 'Reduced', 'Off'], sc.motion, (v) => { setDesktopSpaceComfort({ motion: v as SectorMotion }); this.render(); });
      motionRow.dataset.k = 'flight:motion';
      kb.appendChild(motionRow);
      const scaleRow = segRow('Sector HUD size', HUD_SCALE_CHOICES.map(String), HUD_SCALE_CHOICES.map((v) => `${Math.round(v * 100)}%`), String((HUD_SCALE_CHOICES as readonly number[]).reduce<number>((b, v) => (Math.abs(v - sc.hudScale) < Math.abs(b - sc.hudScale) ? v : b), 1)), (v) => { setDesktopSpaceComfort({ hudScale: Number(v) }); this.render(); });
      scaleRow.dataset.k = 'flight:hud';
      kb.appendChild(scaleRow);
      kb.appendChild(noteRow('System follows your device\'s "reduce motion" setting. Reduced keeps a third of the shake, streaks and FOV kick; Off removes them. Your aim, hits and threat markers are never reduced. The HUD size scales the sector\'s status, target and radar panels only. Both apply at once.'));
    }

    // ── Keyboard ──
    {
      kb.appendChild(el('div', 'op-group', 'Keyboard'));
      const head = el('div', 'op-bind-head');
      head.appendChild(el('div', 'op-note', keyCaptureActive() ? 'Press a key. Esc cancels.' : 'Bindings follow the physical key, so changing the keyboard layout never moves them.'));
      const reset = btn('Reset keyboard', () => { resetKeyBindings(); keyCancelCapture(); this.render(); }, 'op-btn op-btn-small');
      reset.dataset.k = 'key:reset';
      head.appendChild(reset);
      kb.appendChild(head);
      const live = keyBindings();
      // space campaign B: a table saved before an action existed may now mean two things at once — REPORT it (the
      // player chooses which to change; nothing is silently re-bound).
      const clashes = keySavedConflicts();
      for (const c of clashes) {
        const what = [...c.actions.map(actionLabel), ...(c.fixed ? [c.fixed] : [])].join(' and ');
        const warn = el('div', 'op-result is-bad', `⚠ ${keyLabel(c.code)} means two things ${contextLabel(c.frame)}: ${what}. Change one of them.`);
        warn.dataset.keyClash = c.code;
        kb.appendChild(warn);
      }
      const seenKey = new Set<string>();
      for (const [ctx, actions] of Object.entries(KEY_CONTEXTS)) {
        if (actions.every((a) => seenKey.has(a))) continue; // a group whose actions all sit under an earlier heading
        kb.appendChild(el('div', 'op-bind-ctx', contextLabel(ctx)));
        for (const a2 of actions) {
          if (seenKey.has(a2)) continue;
          seenKey.add(a2);
          const row = el('div', 'op-bind');
          row.dataset.action = a2;
          const capturing = keyCaptureActive() === a2;
          const set = btn(capturing ? 'Press a key' : (live[a2].length ? live[a2].map(keyLabel).join(' / ') : 'Unbound'),
            () => { if (keyCaptureActive() === a2) keyCancelCapture(); else keyBeginCapture(a2); this.render(); }, `op-btn op-btn-small op-bind-set${capturing ? ' is-capturing' : ''}`);
          set.dataset.k = `key:${a2}`;
          const clear = btn('Unbind', () => { setKeyBinding(a2, []); this.render(); }, 'op-btn op-btn-small op-bind-clear');
          clear.dataset.k = `key:${a2}:clear`;
          clear.disabled = live[a2].length === 0;
          row.append(el('div', 'op-bind-name', actionLabel(a2)), set, clear);
          kb.appendChild(row);
        }
      }
      const lastK = keyLastCapture();
      if (lastK) {
        const bad = lastK.conflicts.length > 0 || !!lastK.fixed;
        kb.appendChild(el('div', `op-result${bad ? ' is-bad' : ''}`, lastK.fixed
          // space campaign B: a hard-wired shell key (the manual, the chat, the F-row windows) is never re-bound.
          ? `${actionLabel(lastK.action)} cannot take that key: it always opens ${lastK.fixed}.`
          : bad
            ? `${actionLabel(lastK.action)} cannot take that key: ${lastK.conflicts.map(actionLabel).join(', ')} already ${lastK.conflicts.length === 1 ? 'uses' : 'use'} it in the same situation.`
            : lastK.code ? `${actionLabel(lastK.action)} is now ${keyLabel(lastK.code)}.` : `${actionLabel(lastK.action)} was left unchanged.`));
      }
    }

    // ── Gamepad ──
    {
      pad.appendChild(el('div', 'op-group', 'Gamepad'));
      pad.appendChild(toggleRow('Gamepad', padEnabled(), (on) => { setPadEnabled(on); this.render(); }));
      const snap = padSnap();
      const supported = typeof (navigator as Navigator & { getGamepads?: unknown }).getGamepads === 'function';
      pad.appendChild(noteRow(!supported
        ? 'This browser has no gamepad support. It needs a secure page (https or localhost).'
        : snap.present
          ? `Connected: ${snap.id || 'controller'}${snap.mapping === 'standard' ? '' : ' (non-standard layout, mapped by what it can do)'}`
          : padEverSeen()
            ? 'No controller right now.'
            : 'Plug in a controller and press any button on it. Browsers hide controllers until then.'));
      pad.appendChild(toggleRow('Rumble', padRumbleEnabled(), (on) => { setPadRumble(on); if (on) padRumble('hit'); this.render(); }));
      if (padRumbleEnabled() && padSnap().present && !padHasActuator()) pad.appendChild(noteRow('This controller reports no haptics. Firefox and some Bluetooth connections do not expose them.'));
      if (padRumbleEnabled()) pad.appendChild(sliderRow('Rumble strength', 0, 1, 0.05, padRumbleGain(), (v) => `${Math.round(v * 100)}%`, (v) => { setPadRumbleGain(v); padRumble('hit'); }));

      const head = el('div', 'op-bind-head');
      head.appendChild(el('div', 'op-note', padCaptureActive() ? 'Press a button on the controller. Esc cancels.' : 'Choose a button, then press the new one on the controller.'));
      const reset = btn('Reset gamepad', () => { resetPadBindings(); padCancelCapture(); this.render(); }, 'op-btn op-btn-small');
      reset.dataset.k = 'pad:reset';
      head.appendChild(reset);
      pad.appendChild(head);
      const live = padBindings();
      const seenAction = new Set<string>();
      for (const [ctx, actions] of Object.entries(PAD_CONTEXTS)) {
        pad.appendChild(el('div', 'op-bind-ctx', contextLabel(ctx)));
        for (const a of actions) {
          if (seenAction.has(a)) continue;           // `menu` lives in every context
          seenAction.add(a);
          const row = el('div', 'op-bind');
          row.dataset.action = a;
          const capturing = padCaptureActive() === a;
          const set = btn(capturing ? 'Press a button' : live[a].map((i) => PAD_BUTTON_LABEL[i] ?? `#${i}`).join(' / '),
            () => { if (padCaptureActive() === a) padCancelCapture(); else padBeginCapture(a); this.render(); }, `op-btn op-btn-small op-bind-set${capturing ? ' is-capturing' : ''}`);
          set.dataset.k = `pad:${a}`;
          row.append(el('div', 'op-bind-name', actionLabel(a)), set);
          pad.appendChild(row);
        }
      }
      // The outcome of the last rebind: a refusal names the action it would have collided with, because a button
      // meaning two things in one context is the one way this scheme breaks.
      const last = padLastCapture();
      if (last) {
        const bad = last.conflicts.length > 0;
        pad.appendChild(el('div', `op-result${bad ? ' is-bad' : ''}`, bad
          ? `${actionLabel(last.action)} cannot take that button: ${last.conflicts.map(actionLabel).join(', ')} already ${last.conflicts.length === 1 ? 'uses' : 'use'} it in the same situation.`
          : `${actionLabel(last.action)} is now ${PAD_BUTTON_LABEL[last.button ?? -1] ?? 'unbound'}.`));
      }
    }
    cols.append(kb, pad);
    body.appendChild(cols);

    // The sticks and triggers are not rebindable (they are axes, not buttons): say what they do.
    body.appendChild(el('div', 'op-group', 'Sticks and triggers'));
    const help = el('div', 'op-help');
    for (const [where, what] of [
      ['On foot', `Left stick moves, right stick looks. ${padBindingText('jump')} jump, ${padBindingText('crouch')} crouch, ${padBindingText('sprint')} sprint, ${padBindingText('attack')} attack, ${padBindingText('guard')} guard, ${padBindingText('interact')} interact, ${padBindingText('weaponNext')} next weapon. D-pad up and down move the camera in and out.`],
      ['Hovercraft', 'Right trigger is the throttle, left trigger brakes and reverses, the left stick steers. RB boosts, LB is the handbrake.'],
      ['Flying', `Left stick is throttle and roll, right stick points the nose. ${padBindingText('fire')} fire, ${padBindingText('boost')} afterburner, ${padBindingText('levelOut')} level out.`],
      ['Menus', `D-pad or left stick moves, the right stick scrolls. ${padBindingText('uiAccept')} select, ${padBindingText('uiBack')} close, ${padBindingText('uiTabPrev')} and ${padBindingText('uiTabNext')} change tab.`],
      ['Anywhere', `${padBindingText('menu')} opens this menu, ${padBindingText('map')} the map, ${padBindingText('inventory')} the inventory.`],
    ] as const) {
      const line = el('div', 'op-help-line');
      line.append(el('span', 'op-help-where', where), el('span', '', what));
      help.appendChild(line);
    }
    body.appendChild(help);

    // ── Touch controls ── (arrange, 2026-09-16)
    if (matchMedia('(pointer: coarse)').matches || localStorage.getItem('atlas:touch-ui') === 'on') {
      body.appendChild(el('div', 'op-group', 'Touch controls'));
      const input = this.app?.getModule?.('AtlasInput') as { setTouchArrange?: (b: boolean) => void; touchArrangeOn?: () => boolean; touchControls?: () => readonly { id: string; action: string }[]; addTouchControl?: (a: string) => void; removeTouchControl?: (id: string) => void; resetTouchControls?: () => void } | null;
      body.appendChild(toggleRow('Arrange controls', input?.touchArrangeOn?.() ?? false, (v) => { input?.setTouchArrange?.(v); this.render(); }, 'drag to move, drag a corner to resize'));
      for (const c of input?.touchControls?.() ?? []) {
        const row = el('div', 'op-bind');
        const rm = btn('Remove', () => { input?.removeTouchControl?.(c.id); this.render(); }, 'op-btn op-btn-small');
        if (c.action === 'stick') rm.disabled = true;     // without it there is no walking
        row.append(el('div', 'op-bind-name', TOUCH_LABEL[c.action as keyof typeof TOUCH_LABEL]?.name ?? c.action), rm);
        body.appendChild(row);
      }
      const addRow = el('div', 'op-chips');
      for (const a3 of Object.keys(TOUCH_LABEL)) {
        if (a3 === 'stick') continue;
        addRow.appendChild(btn(`Add ${TOUCH_LABEL[a3 as keyof typeof TOUCH_LABEL].name}`, () => { input?.addTouchControl?.(a3); this.render(); }, 'op-btn op-btn-small'));
      }
      addRow.appendChild(btn('Reset touch layout', () => { input?.resetTouchControls?.(); this.render(); }, 'op-btn op-btn-small'));
      body.appendChild(addRow);
    }
  }

  // ── General ── who you are, where you are, what is drawing the picture
  private infoFacts(): Array<[string, Array<[string, string, string?]>]> {
    const n = this.app.renderer.native as unknown as { backend?: { isWebGPUBackend?: boolean; constructor?: { name?: string } }; getDrawingBufferSize(v: THREE.Vector2): THREE.Vector2; getPixelRatio(): number };
    let canvas = 'not ready';
    try { const v = n.getDrawingBufferSize(new THREE.Vector2()); canvas = `${v.x} × ${v.y}, pixel ratio ${n.getPixelRatio().toFixed(2)}`; } catch { /* no renderer yet */ }
    // three's flag, not the class name — a production bundle minifies `WebGPUBackend` (gaps wave, 2026-09-29).
    const backend = n.backend?.isWebGPUBackend === true || n.backend?.constructor?.name === 'WebGPUBackend' ? 'WebGPU' : 'WebGL 2';
    const world = atlas.spaceFlight ? 'Space' : atlas.currentWorldId === null ? 'Not in a world yet'
      : atlas.currentWorldId === atlas.commonsWorldId ? 'The Commons'
        : atlas.currentWorldId === atlas.personalWorldId ? 'Your personal world' : `World ${atlas.currentWorldId}`;
    const conn = atlas.conn as unknown as { isActive?: boolean } | null;
    const pending = draftChanges().length;
    const kb = keyBindings() as unknown as Record<string, string[]>;
    const key = (a: string): string => (kb[a]?.length ? keyLabel(kb[a][0]) : 'unbound');   // read from the LIVE bindings: a rebind shows here
    return [
      ['Traveler', [
        ['Name', atlas.displayName ?? 'No traveler yet'],
        ['Handle', atlas.username ? `@${atlas.username}` : 'None'],
        ['Identity', atlas.identityHex ? `${atlas.identityHex.slice(0, 16)}…` : 'Not signed in'],
      ]],
      ['Where you are', [
        ['World', world],
        ['Position', atlas.currentWorldId === null ? 'None' : `${Math.round(atlas.localX)}, ${Math.round(atlas.localZ)} at ${Math.round(atlas.localY)} m`, 'pos'],
        ['Server', conn ? (conn.isActive === false ? 'Disconnected' : 'Connected') : 'Offline'],
      ]],
      ['Keys at a glance', [
        ['Move', `${key('moveForward')} ${key('moveLeft')} ${key('moveBack')} ${key('moveRight')}`],
        ['Jump, sprint', `${key('jump')}, ${key('sprint')}`],
        ['Crouch', key('crouch')],
        ['Interact', key('interact')],
        ['Attack', `Left click with the mouse captured${kb.attack?.length ? `, or ${key('attack')}` : ''}`],
        ['Guard', 'Hold the right mouse button'],
        ['Map, inventory', `${key('map')}, ${key('inventory')}`],
      ]],
      ['Picture', [
        ['Renderer', backend],
        ['Canvas', canvas],
        ['Graphics preset', `${TIER_LABEL[activeTier()]}${isCustom() ? ', customised' : ''}${pending ? ' (changes waiting on the Graphics tab)' : ''}`],
        ['Frame rate', this.fpsText || 'Measuring', 'fps'],
      ]],
    ];
  }
  private renderGeneral(): void {
    const body = this.body!;
    body.appendChild(this.themePicker());
    const cols = el('div', 'op-cols');
    const facts = this.infoFacts();
    const colA = el('div'), colB = el('div');
    facts.forEach(([title, rows], i) => {
      const host = i < 3 ? colA : colB;
      host.appendChild(el('div', 'op-group', title));
      for (const [k, v, liveKey] of rows) {
        const row = el('div', 'op-fact');
        const val = el('div', 'op-fact-v', v);
        if (liveKey) this.liveEls[liveKey] = val;
        row.append(el('div', 'op-fact-k', k), val);
        host.appendChild(row);
      }
    });
    colB.appendChild(el('div', 'op-group', 'This menu'));
    colB.appendChild(noteRow('Graphics changes wait for the Apply changes button, so any number of them cost one rebuild of the picture. The theme, sound and controls change as you set them.'));
    colB.appendChild(noteRow('Esc closes the menu. With the mouse captured, the first Esc frees the mouse and the second opens the menu.'));
    const copy = btn('Copy this information', () => {
      const text = this.infoFacts().map(([t, rows]) => `${t}\n${rows.map(([k, v]) => `  ${k}: ${v}`).join('\n')}`).join('\n');
      const done = (msg: string): void => { copy.textContent = msg; window.setTimeout(() => { copy.textContent = 'Copy this information'; }, 1800); };
      try { void navigator.clipboard.writeText(text).then(() => done('Copied'), () => done('Copying was blocked by the browser')); } catch { done('Copying was blocked by the browser'); }
    }, 'op-btn');
    copy.dataset.k = 'general:copy';
    copy.title = 'For a bug report';
    colB.appendChild(copy);
    cols.append(colA, colB);
    body.appendChild(cols);
    body.appendChild(this.chatTranslateGroup());
    body.appendChild(this.accountGroup());
  }

  /** THE LOGIN (2026-10-05): the player's account — set / change the password, codes, sign out (here or everywhere). */
  private accountGroup(): HTMLDivElement {
    const wrap = el('div', 'op-account');
    wrap.appendChild(el('div', 'op-group', 'Account'));
    wrap.appendChild(noteRow(`Signed in as ${atlas.username ?? '—'}.`));
    const row = el('div', 'op-account-row');
    if (authStatus.claimed === false) {
      const set = btn('Set a password', () => this.openAuth('claim'), 'op-btn'); set.dataset.k = 'account:claim'; row.appendChild(set);
    } else {
      const ch = btn('Change password', () => this.openAuth('change'), 'op-btn'); ch.dataset.k = 'account:change'; row.appendChild(ch);
      const rg = btn(`Recovery codes: ${authStatus.codesLeft} left — make new`, () => this.openAuth('regen'), 'op-btn'); rg.dataset.k = 'account:regen'; row.appendChild(rg);
    }
    // F1: an UNCLAIMED traveler has no password, so signing out orphans it (Sign in needs one; recovery codes only exist once
    // claimed) — the click explains that and offers "Set a password first" instead of reloading into the sign-in screen.
    const out = btn('Sign out', () => {
      if (authStatus.claimed === true) { clearToken(); location.reload(); return; }
      this.signOutConfirm = !this.signOutConfirm; this.render();
    }, 'op-btn'); out.dataset.k = 'account:signout'; row.appendChild(out);
    if (authStatus.claimed !== false) {
      const all = btn('Sign out everywhere', () => { const t = getToken(); if (!t) { clearToken(); location.reload(); return; }
        void authApi.logoutAll(t).then((r) => { if (r.ok) { clearToken(); location.reload(); } else bus.emit('atlas:toast', { text: r.message, kind: 'error' }); }); }, 'op-btn');
      all.dataset.k = 'account:signout-all'; row.appendChild(all);
    }
    wrap.appendChild(row);
    if (this.signOutConfirm && authStatus.claimed !== true) {
      wrap.appendChild(noteRow('This traveler has no password yet. If you sign out you cannot sign back in — the traveler, its wallet and everything it holds would be left behind. Set a password first and you can sign in from anywhere.'));
      const c = el('div', 'op-account-row');
      const first = btn('Set a password first', () => { this.signOutConfirm = false; this.openAuth('claim'); }, 'op-btn'); first.dataset.k = 'account:signout-claim';
      const anyway = btn('Sign out anyway', () => { clearToken(); location.reload(); }, 'op-btn'); anyway.dataset.k = 'account:signout-anyway';
      const cancel = btn('Cancel', () => { this.signOutConfirm = false; this.render(); }, 'op-btn'); cancel.dataset.k = 'account:signout-cancel';
      c.append(first, anyway, cancel);
      wrap.appendChild(c);
    }
    // G8: nothing warned as the codes ran out — the only way back in from a forgotten password.
    if (authStatus.claimed === true && authStatus.codesLeft <= 2) {
      wrap.appendChild(noteRow(authStatus.codesLeft === 0
        ? 'You have no recovery codes left. If you forget your password you could not get back in — make new codes now.'
        : `Only ${authStatus.codesLeft} recovery code${authStatus.codesLeft === 1 ? '' : 's'} left — make new codes before you need them.`));
    }
    return wrap;
  }

  /** G4 (client sweep 2026-10-05): live chat translation was reachable only by the `/translate` chat command. Applies at once. */
  private chatTranslateGroup(): HTMLDivElement {
    const wrap = el('div', 'op-account');
    wrap.appendChild(el('div', 'op-group', 'Social'));
    const on = translateOn();
    const row = segRow('Chat translation', ['off', 'on'], ['Off', 'On'], on ? 'on' : 'off', (v) => { setTranslateOn(v === 'on'); if (v === 'on') wireChatTranslate(); this.render(); });
    this.helpButton(row, 'Chat translation');
    wrap.appendChild(row);
    const lrow = labeledRow('Chat language');
    const sel = el('select', 'op-select');
    sel.dataset.k = 'chat:lang';
    sel.setAttribute('aria-label', 'Chat language');
    for (const code of supportedLangs()) { const o = el('option'); o.value = code; o.textContent = `${langName(code)} (${code})`; sel.appendChild(o); }
    sel.value = myChatLang();
    sel.addEventListener('change', () => { if (setMyChatLang(sel.value)) { setTranslateOn(true); wireChatTranslate(); this.render(); } });
    lrow.lastElementChild!.appendChild(sel);
    this.helpButton(lrow, 'Chat language');
    wrap.appendChild(lrow);
    return wrap;
  }

  /** The account forms are the #login overlay (z 72), UNDER this menu (z 200): close the menu through its own path FIRST,
   *  or the card opens behind the 760 px panel and the button looks dead (final review F1). */
  private openAuth(mode: 'claim' | 'change' | 'regen'): void {
    if (this.open) this.toggle();
    bus.emit('atlas:auth-open', { mode });
  }

  /** THE THEME PICKER: three cards, each painted in its OWN theme's tokens (a preview of the material, whatever
   *  theme is worn now). A pick applies at once and is remembered — a look is judged by eye, like a sound level. */
  private themePicker(): HTMLDivElement {
    const wrap = el('div', 'op-themes-wrap');
    wrap.appendChild(el('div', 'op-group', 'Interface theme'));
    const grid = el('div', 'op-themes');
    grid.setAttribute('role', 'radiogroup');
    grid.setAttribute('aria-label', 'Interface theme');
    const worn = currentTheme();
    for (const id of THEME_ORDER) {
      const t = THEMES[id];
      const card = btn('', () => { if (currentTheme() !== id) { setTheme(id as ThemeId); this.render(); } }, 'op-theme');
      card.dataset.k = `theme|${id}`;
      card.dataset.theme = id;
      card.setAttribute('role', 'radio');
      card.setAttribute('aria-checked', String(id === worn));
      if (id === worn) card.classList.add('is-worn');
      for (const [k, v] of Object.entries(t.tokens)) card.style.setProperty(k, v); // the card wears its own theme
      const sw = el('span', 'op-theme-sw');
      for (const c of t.swatch) { const dot = el('i'); dot.style.background = c; sw.appendChild(dot); }
      const name = el('span', 'op-theme-name', t.name);
      const tag = el('span', 'op-theme-tag', id === worn ? 'Worn now' : id === DEFAULT_THEME ? 'Default' : '');
      const head = el('span', 'op-theme-head');
      head.append(name, tag);
      card.append(sw, head, el('span', 'op-theme-blurb', t.blurb));
      grid.appendChild(card);
    }
    wrap.appendChild(grid);
    wrap.appendChild(noteRow('Every panel, window and menu wears the theme. It applies straight away and is remembered on this device.'));
    return wrap;
  }

  private currentFov(): number {
    try { return (this.app.renderer.cameras.main as unknown as { fov?: number }).fov ?? 70; } catch { return 70; }
  }
  /** Apply the persisted FOV to the live camera (boot + slider). */
  private applyFov(): void {
    const deg = activePreset().cameraFov ?? 0;
    if (deg <= 0) return;
    try {
      const cam = this.app.renderer.cameras.main as unknown as { fov: number; updateProjectionMatrix(): void };
      cam.fov = deg;
      cam.updateProjectionMatrix();
    } catch { /* no perspective camera yet */ }
  }

  // ── Auto-tune: walk the preset ladder to hold playable frame rates.
  // Down at <42 fps sustained 3 s (20 s cooldown); up at >57 fps sustained
  // 60 s. Custom settings are respected — the tuner never discards them.
  private tuneBelow = 0;
  private tuneAbove = 0;
  private tuneCooldownMs = 0;

  private updateFps(dtSeconds: number): void {
    this.fpsAccumMs += dtSeconds * 1000;
    this.fpsFrames++;
    if (this.tuneCooldownMs > 0) this.tuneCooldownMs -= dtSeconds * 1000;
    if (this.fpsAccumMs >= 500) {
      const avgMs = this.fpsAccumMs / this.fpsFrames;
      const fps = 1000 / avgMs;
      this.fpsText = `${Math.round(fps)} fps, ${avgMs.toFixed(1)} ms a frame`;
      if (this.open && this.fpsEl) {
        const [a, b] = Array.from(this.fpsEl.children) as HTMLElement[];
        if (a) a.textContent = `${Math.round(fps)} fps`;
        if (b) b.textContent = `${avgMs.toFixed(1)} ms`;
        if (this.liveEls.fps) this.liveEls.fps.textContent = this.fpsText;
        if (this.liveEls.pos && atlas.currentWorldId !== null) this.liveEls.pos.textContent = `${Math.round(atlas.localX)}, ${Math.round(atlas.localZ)} at ${Math.round(atlas.localY)} m`;
      }
      this.fpsAccumMs = 0;
      this.fpsFrames = 0;
      if ((activePreset().autoTune ?? false) && !isCustom() && this.tuneCooldownMs <= 0 && !document.hidden) {
        this.tuneBelow = fps < 42 ? this.tuneBelow + 1 : 0;
        this.tuneAbove = fps > 57 ? this.tuneAbove + 1 : 0;
        const idx = TIER_ORDER.indexOf(activeTier());
        if (this.tuneBelow >= 6 && idx > 0) {
          this.postMod()?.setTier(TIER_ORDER[idx - 1]);
          setAutoTune(true); // setTier resets to the preset — keep the tuner armed
          this.tuneBelow = 0; this.tuneAbove = 0; this.tuneCooldownMs = 20_000;
          if (this.open) this.render();
        } else if (this.tuneAbove >= 120 && idx < TIER_ORDER.length - 1) {
          this.postMod()?.setTier(TIER_ORDER[idx + 1]);
          setAutoTune(true);
          this.tuneBelow = 0; this.tuneAbove = 0; this.tuneCooldownMs = 30_000;
          if (this.open) this.render();
        }
      }
    }
  }

  dispose(): void {
    if (this.keyHandler) { window.removeEventListener('keydown', this.keyHandler, true); this.keyHandler = null; }
    this.overlay?.remove();
    this.overlay = null;
    this.body = null; this.tabsEl = null; this.railEl = null; this.footEl = null;
    this.fpsEl = null;
    document.getElementById('atlas-options-style')?.remove();
  }
}

// ─── DOM helpers ──────────────────────────────────────────────────────────────

type Tab = 'general' | 'graphics' | 'sound' | 'controls';
const TABS: ReadonlyArray<[Tab, string]> = [['general', 'General'], ['graphics', 'Graphics'], ['sound', 'Sound'], ['controls', 'Controls']];

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

function btn(label: string, onClick: () => void, cls = ''): HTMLButtonElement {
  const b = el('button', cls, label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

/** 'weaponNext' → 'Weapon next'; 'uiAccept' → 'Menu accept' — the binding tables name actions for code, not for people. */
const ACTION_LABEL: Record<string, string> = {
  // the sector (space campaign B): named for what they do, the id kept in data-action
  spaceLand: 'Dock / land / planetfall', spaceTarget: 'Cycle threat targets', spaceMatchSpeed: 'Match target speed',
  spaceNav: 'Cycle nav points', spaceHyper: 'Hyperdrive to nav', spaceGalaxy: 'Galaxy chart', spaceSystemMap: 'System chart',
  spaceRepair: 'Repair at a station or depot', spaceDock: 'Station dock window', spaceBoard: 'Board / leave a station',
  spaceDeck: 'Walk / leave the Sovereign deck', spaceTurret: 'Take / leave a Sovereign turret', spaceMissile: 'Secondary fire (missile / fire group)',
  spaceFireGroup: 'Swap fire group (1 ↔ 2)', // space campaign I: the secondary trigger fires the active group's mount
  spaceEngineering: 'Engineering panel (subsystems, crew, power)', spacePatch: 'Field patch (one repair kit, 8 s)', // space campaign G
  spaceCountermeasures: 'Countermeasures (flares · sensor burst)', spaceSubTarget: 'Subsystem targeting', spaceDroid: 'Droid emergency patch',
  spaceCockpit: 'Cockpit / chase view', spaceScan: 'Exploration scan ping', spaceOps: 'Ops panel (squadron, front)',
  spaceEscort: 'Escort a firm\'s run', spaceBeam: 'Utility beam (mine, salvage, recover)', spacePowerWeapons: 'Power to weapons',
  spacePowerShields: 'Power to shields', spacePowerEngines: 'Power to engines', spacePowerBalanced: 'Power balanced',
  spaceFurnish: 'Furnish the deck', spaceSurvey: 'Survey desk (sites, tenders, log, intel)', levelOut: 'Level the wings', rollLeft: 'Roll left', rollRight: 'Roll right',
  weaponNext: 'Next weapon', weaponPrev: 'Previous weapon', zoomIn: 'Camera closer', zoomOut: 'Camera farther',
  uiAccept: 'Menu select', uiBack: 'Menu back', uiTabNext: 'Menu next tab', uiTabPrev: 'Menu previous tab',
  editToolNext: 'Edit next tool', editToolPrev: 'Edit previous tool', throttleUp: 'Throttle up', throttleDown: 'Throttle down',
  voicePtt: 'Push to talk (voice rooms)',
};
function actionLabel(a: string): string {
  if (ACTION_LABEL[a]) return ACTION_LABEL[a];
  const words = a.replace(/^ui(?=[A-Z])/, 'menu ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
const CONTEXT_LABEL: Record<string, string> = {
  foot: 'On foot', vehicle: 'Hovercraft', flight: 'Flying', ui: 'Menus', edit: 'Edit mode',
  // space campaign B: the sector's contexts, and the frames a saved clash names
  world: 'On the ground and in the air', space: 'The sector (flying a ship in space)', spaceWalk: 'Walking a station or the Sovereign deck',
  docked: 'Docked at a station', onFoot: 'on foot', riding: 'in a hovercraft', skyFlight: 'in atmospheric flight',
  sector: 'in the sector', sectorWalk: 'walking a station or deck', sectorDocked: 'while docked',
};
function contextLabel(ctx: string): string { return CONTEXT_LABEL[ctx] ?? actionLabel(ctx); }

/** A row: the label (with an optional quiet note) on the left, the control cell on the right. */
function labeledRow(label: string, when = '', was = ''): HTMLDivElement {
  const row = el('div', 'op-row');
  const l = el('div', 'op-label', label);
  if (when) l.appendChild(el('span', 'op-when', when));
  const ctl = el('div', 'op-ctl');
  if (was) ctl.appendChild(el('span', 'op-was', was));
  row.append(l, ctl);
  return row;
}

/** A label + a row of segmented option buttons; the current option is filled. */
function segRow(label: string, values: readonly string[], labels: readonly string[], current: string, onPick: (v: string) => void, when = '', was = ''): HTMLDivElement {
  const row = labeledRow(label, when, was);
  const seg = el('div', 'op-seg');
  seg.setAttribute('role', 'group');
  seg.setAttribute('aria-label', label);
  values.forEach((v, i) => {
    const b = btn(labels[i], () => onPick(v));
    b.dataset.k = `${label}|${v}`;        // the re-render hands the focus back to this control
    b.setAttribute('aria-pressed', String(v === current));
    if (v === current) b.classList.add('on');
    seg.appendChild(b);
  });
  row.lastElementChild!.appendChild(seg);
  return row;
}

function toggleRow(label: string, on: boolean, onChange: (on: boolean) => void, when = ''): HTMLDivElement {
  return segRow(label, ['off', 'on'], ['Off', 'On'], on ? 'on' : 'off', (v) => onChange(v === 'on'), when);
}

/** A label + a slider + a live value readout; fires onChange on release (not every tick). */
function sliderRow(label: string, min: number, max: number, step: number, value: number, fmt: (v: number) => string, onChange: (v: number) => void, live = false, when = '', was = ''): HTMLDivElement {
  const row = labeledRow(label, when, was);
  const slider = el('input', 'op-range');
  slider.type = 'range';
  slider.min = String(min); slider.max = String(max); slider.step = String(step); slider.value = String(value);
  slider.dataset.k = `${label}|range`;
  slider.setAttribute('aria-label', label);
  const val = el('span', 'op-val', fmt(value));
  // 'input' updates the readout while dragging; 'change' (release) commits — staging a graphics row per tick would
  // re-render the list under the pointer. `live` (the audio volumes) applies while dragging too: a level is set by ear.
  slider.addEventListener('input', () => { const v = parseFloat(slider.value); val.textContent = fmt(v); if (live) onChange(v); });
  slider.addEventListener('change', () => onChange(parseFloat(slider.value)));
  row.lastElementChild!.append(slider, val);
  return row;
}

/** A quiet explanatory line under a control. */
function noteRow(text: string): HTMLDivElement { return el('div', 'op-note', text); }

// The Esc menu wears the game's own slate (the loading screen, the traveler screen): burnt-umber ink, a brass seam,
// the stencil wordmark face for the title, Sora for reading, Plex Mono for the numbers only. The ONE loud element is
// the Apply bar — a quiet line when there is nothing to apply, a solid brass strip when changes are waiting.
const OPTIONS_CSS = `
#atlas-options{position:fixed;inset:0;display:none;align-items:center;justify-content:center;z-index:200;background:var(--ax-scrim);
  --ink:var(--ax-bg);--ink2:var(--ax-bg2);--ink3:var(--ax-bg3);--line:var(--ax-line);--line2:var(--ax-line2);--line-weak:color-mix(in srgb, var(--ax-line) 70%, var(--ax-bg));
  --bone:var(--ax-text);--soft:var(--ax-soft);--dust:var(--ax-muted);--brass:var(--ax-accent);--rust:var(--ax-alert);--cyan:var(--ax-info);--onbrass:var(--ax-on-accent);
  font:400 14px/1.45 var(--ax-font);color:var(--bone)}
#atlas-options *{box-sizing:border-box}
#atlas-options .op-panel{width:min(760px,96vw);height:min(86vh,820px);display:flex;flex-direction:column;background-color:var(--ax-win-bg);
  background-image:var(--ax-grain);border:var(--ax-win-border);border-left:var(--ax-seam-left);border-radius:var(--ax-win-radius);overflow:hidden;
  box-shadow:var(--ax-win-frame),0 22px 70px rgba(0,0,0,.5);-webkit-backdrop-filter:var(--ax-win-blur);backdrop-filter:var(--ax-win-blur);
  outline:var(--ax-win-outline);outline-offset:-4px}
#atlas-options .op-head{border-top:var(--ax-seam-top)}
#atlas-options .op-head{display:flex;align-items:center;gap:16px;padding:16px 20px 2px 22px}
#atlas-options .op-title{font:700 32px/1 var(--ax-display);letter-spacing:.03em;color:var(--brass)}
#atlas-options .op-fps{margin-left:auto;display:flex;gap:14px;font:500 12px/1 var(--ax-mono);color:var(--dust);font-variant-numeric:tabular-nums}
#atlas-options button{font:inherit;color:inherit;cursor:pointer}
#atlas-options button:disabled{cursor:default}
#atlas-options :focus-visible{outline:2px solid var(--cyan);outline-offset:2px}
#atlas-options .op-btn{background:var(--ink2);border:1px solid var(--line2);border-radius:var(--ax-radius);padding:6px 13px;font-size:13px;font-weight:500;color:var(--bone)}
#atlas-options .op-btn:hover:not(:disabled){border-color:var(--dust);background:var(--ink3)}
#atlas-options .op-btn:disabled{opacity:.4}
#atlas-options .op-btn-small{padding:4px 10px;font-size:12.5px}
#atlas-options .op-tabs{display:flex;gap:4px;padding:10px 20px 0 18px;border-bottom:1px solid var(--line)}
#atlas-options .op-tab{background:none;border:0;border-bottom:3px solid transparent;margin-bottom:-1px;padding:9px 14px 9px;font-size:15px;font-weight:600;color:var(--dust)}
#atlas-options .op-tab:hover{color:var(--soft)}
#atlas-options .op-tab[aria-selected="true"]{color:var(--bone);border-bottom-color:var(--brass)}
#atlas-options .op-tab[data-dirty]::after,#atlas-options .op-rail-link[data-dirty]::after{content:'';display:inline-block;flex:none;width:7px;height:7px;border-radius:50%;background:var(--rust);margin-left:8px;vertical-align:1px}
#atlas-options .op-rail-link[data-dirty]::after{margin:0 10px 0 0}
#atlas-options .op-main{flex:1;min-height:0;display:flex}
#atlas-options .op-rail{flex:none;width:192px;padding:12px 0 12px 10px;border-right:1px solid var(--line-weak);overflow-y:auto;scrollbar-width:none}
#atlas-options .op-rail-link{display:flex;align-items:center;justify-content:space-between;gap:8px;width:100%;text-align:left;white-space:nowrap;background:none;border:0;border-left:2px solid transparent;padding:6px 8px 6px 12px;font-size:13px;color:var(--dust)}
#atlas-options .op-rail-link:hover{color:var(--soft)}
#atlas-options .op-rail-link.is-here{color:var(--bone);border-left-color:var(--brass)}
#atlas-options .op-body{flex:1;min-width:0;overflow-y:auto;padding:2px 22px 20px 24px;scrollbar-width:thin;scrollbar-color:var(--line2) transparent}
#atlas-options .op-group{margin:18px 0 6px;padding-bottom:6px;border-bottom:1px solid var(--line-weak);font-size:13px;font-weight:600;color:var(--brass)}
#atlas-options .op-row{display:flex;align-items:center;justify-content:space-between;gap:14px;min-height:34px;padding:3px 0 3px 10px;margin-left:-12px;border-left:2px solid transparent}
#atlas-options .op-row.is-changed{border-left-color:var(--rust)}
#atlas-options .op-row-wide{justify-content:flex-start;flex-wrap:wrap}
#atlas-options .op-label{min-width:0;color:var(--soft);font-size:13.5px}
#atlas-options .op-help{display:inline-flex;align-items:center;justify-content:center;flex:none;width:17px;height:17px;margin:0 0 0 7px;padding:0;vertical-align:1px;border-radius:50%;border:1px solid var(--line2);background:var(--ink2);color:var(--dust);font:700 11px/1 var(--ax-font)}
#atlas-options .op-help:hover,#atlas-options .op-help[aria-expanded="true"]{color:var(--onbrass);background:var(--brass);border-color:var(--brass)}
#atlas-options .op-row-wide .op-help{margin:0 4px}
#atlas-options .op-tip{position:fixed;z-index:1;display:none;width:max-content;max-width:min(360px,calc(100vw - 24px));padding:10px 13px 11px;background-color:var(--ax-win-bg);background-image:var(--ax-grain);border:var(--ax-win-border);border-top:2px solid var(--brass);border-radius:var(--ax-win-radius);box-shadow:var(--ax-win-frame),0 12px 32px rgba(0,0,0,.45);-webkit-backdrop-filter:var(--ax-win-blur);backdrop-filter:var(--ax-win-blur)}
#atlas-options .op-tip-title{margin-bottom:4px;font:600 13px/1.3 var(--ax-font);color:var(--brass)}
#atlas-options .op-tip-text{font:400 12.5px/1.55 var(--ax-font);color:var(--bone)}
#atlas-options .op-when{margin-left:9px;font-size:12px;color:var(--dust)}
#atlas-options .op-ctl{flex:none;display:flex;align-items:center;gap:10px}
#atlas-options .op-was{font-size:12px;color:var(--ax-bad);white-space:nowrap}
#atlas-options .op-seg{display:inline-flex;border:1px solid var(--line2);border-radius:var(--ax-radius);overflow:hidden;background:var(--ink2)}
#atlas-options .op-seg button{background:none;border:0;border-right:1px solid var(--line2);padding:4px 10px;font-size:12.5px;font-weight:500;color:var(--soft);white-space:nowrap}
#atlas-options .op-seg button:last-child{border-right:0}
#atlas-options .op-seg button:hover{background:var(--ink3)}
#atlas-options .op-seg button.on{background:var(--brass);color:var(--onbrass);font-weight:600}
#atlas-options .op-seg button.base{box-shadow:inset 0 -2px 0 var(--brass);color:var(--bone)}
#atlas-options .op-seg button:focus-visible{outline-offset:-2px}
#atlas-options .op-range{width:190px;accent-color:var(--brass)}
#atlas-options .op-val{min-width:60px;text-align:right;font:500 12px/1 var(--ax-mono);color:var(--bone);font-variant-numeric:tabular-nums}
#atlas-options .op-select{background:var(--ink2);color:var(--bone);border:1px solid var(--line2);border-radius:var(--ax-radius);padding:4px 8px;font-size:12.5px;max-width:240px}
#atlas-options .op-account-row{display:flex;flex-wrap:wrap;gap:8px;margin:4px 0 12px}
#atlas-options .op-note{margin:2px 0 8px;font-size:12.5px;line-height:1.5;color:var(--dust);max-width:64ch}
#atlas-options .op-cols{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:0 30px;align-items:start}
#atlas-options .op-cols .op-range{width:120px}
#atlas-options .op-cols .op-when{display:block;margin:1px 0 0}
#atlas-options .op-fact{display:flex;gap:12px;padding:5px 0;font-size:13.5px}
#atlas-options .op-fact-k{flex:none;width:118px;color:var(--dust)}
#atlas-options .op-fact-v{min-width:0;color:var(--bone);overflow-wrap:anywhere}
#atlas-options .op-bind-head{display:flex;align-items:flex-start;gap:10px;margin:2px 0 4px}
#atlas-options .op-bind-head .op-note{flex:1;margin:0}
#atlas-options .op-bind-ctx{margin:12px 0 2px;font-size:12.5px;font-weight:600;color:var(--soft)}
#atlas-options .op-bind{display:flex;align-items:center;gap:6px;padding:2px 0}
#atlas-options .op-bind-name{flex:1;min-width:0;font-size:13px;color:var(--soft)}
#atlas-options .op-bind-set{min-width:92px;text-align:center}
#atlas-options .op-bind-set.is-capturing{border-color:var(--brass);color:var(--brass)}
#atlas-options .op-bind-clear{background:none;border-color:transparent;color:var(--dust);padding:4px 6px}
#atlas-options .op-bind-clear:hover:not(:disabled){background:none;border-color:transparent;color:var(--bone);text-decoration:underline}
#atlas-options .op-bind-clear:disabled{visibility:hidden}
#atlas-options .op-result{margin-top:10px;font-size:12.5px;color:var(--ax-good)}
#atlas-options .op-result.is-bad{color:var(--ax-bad)}
#atlas-options .op-help{font-size:13px;color:var(--soft)}
#atlas-options .op-help-line{display:flex;gap:12px;padding:4px 0;line-height:1.5}
#atlas-options .op-help-where{flex:none;width:96px;color:var(--dust)}
#atlas-options .op-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px}
#atlas-options .op-apply{display:flex;align-items:center;gap:10px;padding:12px 20px 12px 22px;border-top:1px solid var(--line);background:var(--ink2);color:var(--dust)}
#atlas-options .op-apply-text{flex:1;min-width:0;font-size:13.5px;font-weight:600}
#atlas-options .op-apply.is-pending{background:var(--brass);border-top-color:var(--brass);color:var(--onbrass)}
#atlas-options .op-apply.is-pending .op-apply-go{background:var(--onbrass);border-color:var(--onbrass);color:var(--bone);padding:8px 20px;font-weight:600}
#atlas-options .op-apply.is-pending .op-apply-go:hover{background:color-mix(in srgb, var(--onbrass) 86%, white)}
#atlas-options .op-apply.is-pending .op-apply-discard{background:transparent;border-color:color-mix(in srgb, var(--onbrass) 55%, transparent);color:var(--onbrass)}
#atlas-options .op-apply.is-pending .op-apply-discard:hover{background:color-mix(in srgb, var(--onbrass) 12%, transparent)}
#atlas-options .op-apply.is-pending :focus-visible{outline-color:var(--onbrass)}
#atlas-options .op-themes-wrap{margin-bottom:4px}
#atlas-options .op-themes{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:10px 0 4px}
#atlas-options .op-theme{display:flex;flex-direction:column;align-items:stretch;gap:9px;min-height:122px;padding:12px 13px 13px;text-align:left;
  background-color:var(--ax-bg);background-image:var(--ax-grain);color:var(--ax-text);font-family:var(--ax-font);
  border:1px solid var(--ax-line);border-left:var(--ax-seam-left);border-top:var(--ax-seam-top);border-radius:var(--ax-win-radius);
  outline:var(--ax-win-outline);outline-offset:-4px;box-shadow:0 0 0 1px transparent}
#atlas-options .op-theme:hover{border-color:var(--ax-line2)}
#atlas-options .op-theme.is-worn{box-shadow:0 0 0 2px var(--brass)}
#atlas-options .op-theme:focus-visible{outline:2px solid var(--cyan);outline-offset:2px}
#atlas-options .op-theme-sw{display:flex;gap:5px}
#atlas-options .op-theme-sw i{display:block;width:16px;height:16px;border-radius:50%;border:1px solid var(--ax-line2)}
#atlas-options .op-theme-head{display:flex;align-items:baseline;justify-content:space-between;gap:8px}
#atlas-options .op-theme-name{font:700 21px/1 var(--ax-display);letter-spacing:.02em;color:var(--ax-hi)}
#atlas-options .op-theme-tag{font-size:11.5px;color:var(--ax-muted);white-space:nowrap}
#atlas-options .op-theme.is-worn .op-theme-tag{color:var(--ax-text)}
#atlas-options .op-theme-blurb{font-size:12.5px;line-height:1.45;color:var(--ax-soft)}
@media (prefers-reduced-motion:no-preference){#atlas-options .op-apply{transition:background-color .18s ease,color .18s ease}}
@media (max-width:720px){
  #atlas-options .op-panel{height:94vh}
  #atlas-options .op-main{flex-direction:column}
  #atlas-options .op-rail{width:auto;display:flex;gap:2px;overflow-x:auto;padding:6px 12px;border-right:0;border-bottom:1px solid var(--line-weak)}
  #atlas-options .op-themes{grid-template-columns:minmax(0,1fr)}
  #atlas-options .op-rail-link{width:auto;white-space:nowrap;border-left:0;border-bottom:2px solid transparent;padding:5px 9px}
  #atlas-options .op-rail-link.is-here{border-bottom-color:var(--brass)}
  #atlas-options .op-body{padding:2px 14px 18px 18px}
  #atlas-options .op-row{flex-wrap:wrap;gap:4px 14px}
  #atlas-options .op-cols{grid-template-columns:minmax(0,1fr)}
  #atlas-options .op-fps{display:none}
  #atlas-options .op-tab{padding:9px 10px;font-size:14px}
}
`;
